import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FieldRelationshipService } from '../../../services/field-relationships/field-relationship.service';
import { FormsModule } from '@angular/forms';
import {
  FormDynamicApiSelectComponent,
  FormDynamicApiTableComponent,
  FormDatetimeComponent,
  FormTextInputComponent,
  FormSelectComponent,
  MultiCheckboxComponent,
  FormTextareaComponent,
  FormLocationComponent,
  FormPhotoUploadComponent,
  FormAvatarUploadComponent,
  FormDateComponent,
  FormDateRangeComponent,
  FormSignatureComponent,
  FormRadiobuttonComponent,
  FormNumberInputComponent,
  FormParagraphComponent,
  FormTimestampComponent,
  FormDepartmentSelectorComponent,
  FormPersonnelSelectorComponent,
  FormApprovalPersonnelSelectorComponent,
  FormTagsComponent,
  FormApprovalFlowComponent,
  FormVideoPlayerComponent,
  FormAudioRecorderComponent,
  MeetingSignatureCollectionComponent,
  FillInFieldComponent,
  FormSubformRelationComponent,
  RiskAssessmentMatrixComponent,
} from '../../items/total-components';

import { FormRichTextDisplayComponent } from '../../items/form-rich-text-display.component';

import { FileDisplayComponent, FileDisplayItem } from '../../items/file-display/file-display.component';
import { FileUploadManagerComponent } from '../../items/file-upload-manager/file-upload-manager.component';
import { FileItem } from '../../items/file-upload-manager/models/file-item.interface';
import { RequiredDocumentUploadComponent } from '../../items/required-document-upload/required-document-upload.component';

import { DatabaseService } from '../../../services/database.service';
import { FormConfigService } from '../../../services/form-config.service';
import { AssistantUrlService } from '../../../services/assistant-url.service';
import { InitiatorInfo } from '../../items/approval/models/approval-flow.model';
import { AuthService } from '../../../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../services/global-environment';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';
import { EmailService } from '../../../services/email.service';
import { StageProgressComponent } from '../../stage-progress/stage-progress.component';
import { DepartmentService } from '../../../services/department.service';
import { FormAutoNumberingComponent } from '../../items/form-auto-numbering/form-auto-numbering.component';
import { GuidService } from '../../../services/guid.service';

// 定義階段配置的介面
export interface FormStage {
  id: string;
  name: string;
  description: string;
  order: number;
  requiredDepartments: string[];
  allowedUsers: string[];
  fields: string[];
  status: 'pending' | 'in_progress' | 'completed' | 'approved';
  completedBy?: string;
  completedAt?: Date;
}

// 定義增強表單值的介面
export interface EnhancedFormValue {
  type: string;
  value: string | object | any[];  // 顯示名稱或完整物件
  id?: string | any[];            // 唯一ID（可選）
}

// 定義動態API表格選擇欄位的介面
export interface SelectColumn {
  field: string;  // 資料庫欄位代碼
  alias: string;  // 顯示名稱
}

// 定義欄位配置的介面
export interface FormFieldConfig {
  type: 'datetime' | 'select' | 'radiobutton' | 'textarea' | 'location' | 'photo-upload' | 'date' | 'date-range' | 'signature' | 'text-input' | 'number-input' | 'multi-checkbox' | 'paragraph' | 'timestamp' | 'department-selector' | 'personnel-selector' | 'approval-personnel-selector' | 'tags' | 'approval-flow' | 'file-display' | 'audio-recorder' | 'video-player' | 'meeting-signature-collection' | 'file-upload-manager' | 'video-player' | 'fill-in' | 'rich-text-display'| 'dynamic-api-select' | 'dynamic-api-table' | 'subform-relation' | 'required-document-upload' | 'avatar-upload' | 'auto-numbering' | 'risk-assessment-matrix' | 'status';
  label: string;
  id: string;
  name: string;
  placeholder?: string;
  options?: any[];
  required?: boolean;
  content?: string; // 新增：用於段落元件的內容
  textAlign?: 'left' | 'center' | 'right' | 'justify'; // 新增：用於段落元件和富文本顯示元件的對齊方式
  format?: 'date' | 'time' | 'datetime'; // 新增：用於時間戳記元件的格式
  multiSelect?: boolean; // 新增：用於人員選擇器的多選模式
  // 新增：用於人員選擇器的部門篩選功能
  filterByDepartment?: boolean; // 是否啟用部門篩選
  sourceComponentName?: string; // 來源部門元件名稱
  departmentField?: string; // 部門欄位名稱
  enableMemory?: boolean; // 新增：用於簽核人員選擇器的記憶功能
  memoryKey?: string; // 新增：用於簽核人員選擇器的記憶鍵值
  approvalFlowId?: string; // 新增：用於簽核流程元件的流程 ID
  files?: FileDisplayItem[]; // 新增：用於 file-display 元件的檔案資料
  // 新增：用於檔案上傳管理元件的配置
  fileUploadConfig?: {
    maxFiles?: number;
    maxFileSize?: number;
    allowedTypes?: string[];
    enableDragDrop?: boolean;
    enablePreview?: boolean;
    allowRename?: boolean;
    showFileSize?: boolean;
    showUploadTime?: boolean;
    uploadButtonText?: string;
    dragDropText?: string;
  };
  // 新增：用於指定證件上傳元件的配置
  requiredDocumentConfig?: {
    documentName?: string;
    documentDescription?: string;
    aiParsing?: boolean;
    expiryDateField?: string;
    acceptedFormats?: string[];
    maxFileSize?: number;
    uploadHint?: string;
    showFileSize?: boolean;
    showUploadTime?: boolean;
    enableDragDrop?: boolean;
    enablePreview?: boolean;
  };
  // 新增：用於影片播放器元件的屬性
  youtubeUrl?: string; // YouTube 影片連結
  videoTitle?: string; // 影片標題
  videoDescription?: string; // 影片描述
  videoWidth?: string; // 影片寬度
  videoHeight?: string; // 影片高度
  videoAutoplay?: boolean; // 自動播放
  videoControls?: boolean; // 顯示控制項
  videoAllowFullscreen?: boolean; // 允許全螢幕
  maxDuration?: number; // 新增：用於錄音元件的最大時長
  minDuration?: number; // 新增：用於錄音元件的最小時長
  audioQuality?: 'high' | 'medium' | 'low'; // 新增：用於錄音元件的音質設定
  audioFormat?: 'mp3' | 'wav'; // 新增：用於錄音元件的音訊格式
  conditionalInputs?: any; // 新增：用於 radiobutton 元件的條件式輸入配置
  questionImageUrl?: string; // 新增：用於 radiobutton 元件的題目圖片 URL
  questionImageBlobName?: string; // 新增：用於 radiobutton 元件的 Azure Blob 名稱
  questionImageAltText?: string; // 新增：用於 radiobutton 元件的圖片替代文字（無障礙）
  // 新增：用於多人簽名元件的屬性
  documentId?: string; // 文件 ID
  title?: string; // 簽名標題
  participants?: any[]; // 參與者清單
  // 新增：用於填空元件的屬性
  template?: string; // 填空模板（向後相容）
  rows?: any[]; // 多列模板配置
  fields?: any[]; // 填空欄位配置
  // 新增：用於富文本顯示元件的屬性
  fontSize?: string; // 字型大小
  color?: string; // 文字顏色
  lineHeight?: string; // 行間距
  maxWidth?: string; // 最大寬度
  backgroundColor?: string; // 背景顏色
  padding?: string; // 內邊距
  borderRadius?: string; // 圓角設定
  // 新增：用於動態API相關元件的屬性
  formId?: string; // 表單ID（動態API下拉選單和子表單關聯元件共用）
  labelField?: string; // 顯示欄位名稱（用於動態API下拉選單元件）
  valueField?: string; // 值欄位名稱（用於動態API下拉選單元件）
  enableWhereCondition?: boolean; // 是否啟用where條件（用於動態API下拉選單元件和多選框元件）
  whereSourceComponent?: string; // where條件來源元件名稱（用於動態API下拉選單元件和多選框元件）
  whereTargetField?: string; // where條件目標欄位名稱（用於動態API下拉選單元件）
  whereTargetValue?: string; // where條件目標值（用於多選框元件）
  selectcols?: SelectColumn[]; // 欄位設定陣列（用於動態API表格元件）
  apiKey?: string; // API呼叫的key值（用於動態API表格元件）
  maxSelections?: number; // 動態API表格的最大選擇數量
  // 新增：用於階段化表單的屬性
  stageId?: string; // 歸屬的階段 ID
  // 新增：用於表單建構器的階段配置（作為陣列的擴展屬性）
  formStages?: FormStage[]; // 表單的階段配置
  // 新增：用於標記系統自動生成的元件（如簽核意見、簽名）
  _isSystemGenerated?: boolean;

  // 新增：用於自動編號元件的配置
  autoNumbering?: {
    elements: Array<{
      type: 'dropdown' | 'sequence' | 'year' | 'month' | 'text';
      order: number;
      config: any;
    }>;
    separator?: string;
    preview?: string;
    apiKey?: string;
    sequenceConfig?: {
      digits: number;
      startFrom: number;
      apiKey?: string;
    };
  };

  // 新增：通用配置屬性，用於各種元件的特殊配置
  config?: any;

  // 新增：用於日期範圍元件的屬性
  dateType?: 'date' | 'datetime';
  startLabel?: string;
  endLabel?: string;
  allowSameDate?: boolean;
  minDate?: string;  // 最小日期（YYYY-MM-DD 格式）
  maxDate?: string;  // 最大日期（YYYY-MM-DD 格式）
  hint?: string;     // 提示訊息（用於說明日期限制原因）

}

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  // 請根據你的專案補上各通用元件的 import
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // 直接匯入表單元件
    FormDynamicApiSelectComponent,
    FormDynamicApiTableComponent,
    FormDatetimeComponent,
    FormTextInputComponent,
    FormSelectComponent,
    MultiCheckboxComponent,
    FormTextareaComponent,
    FormLocationComponent,
    FormPhotoUploadComponent,
    FormAvatarUploadComponent,
    FormDateComponent,
    FormDateRangeComponent,
    FormSignatureComponent,
    FormRadiobuttonComponent,
    FormNumberInputComponent,
    FormParagraphComponent,
    FormTimestampComponent,
    FormDepartmentSelectorComponent,
    FormPersonnelSelectorComponent,
    FormApprovalPersonnelSelectorComponent,
    FormTagsComponent,
    FormApprovalFlowComponent,
    FormVideoPlayerComponent,
    FileDisplayComponent,
    FileUploadManagerComponent,
    RequiredDocumentUploadComponent,
    FormAudioRecorderComponent,
    MeetingSignatureCollectionComponent,
    FillInFieldComponent,
    FormRichTextDisplayComponent,
    FormSubformRelationComponent,
    FormAutoNumberingComponent,
    RiskAssessmentMatrixComponent,
    TranslatePipe,
    StageProgressComponent
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() formName: string = '';
  @Input() assistantid: string = '';
  @Input() assistantname: string = '';
  @Input() config: FormFieldConfig[] = [];
  @Input() preview: boolean = false; //Preview模式主要為在編輯的狀態
  @Input() getuseridlist: boolean = false; // 新增：是否需要取得使用者列表
  @Input() editMode: boolean = false; // 新增：是否為編輯模式
  @Input() editFormId: string = ''; // 新增：編輯的表單ID
  @Input() initialValues: any = {}; // 新增：初始值
  @Input() parent_formid: string = ''; // 新增：父表單ID

  @Input() formid: string = ''; // 新增：表單配置的 ID

  @Input() ExtraSaveDate: any = {}; // 新增：額外儲存的資料，主要用於簽核流程中儲存額外資訊

  // 新增：主表單日期範圍限制（用於限制子表單的日期欄位）
  @Input() parentDateRange?: {
    startDate: string;  // 主表單作業開始日期
    endDate: string;    // 主表單作業結束日期
  };

  //TODO:這四個屬性要合併
  @Input() approvalMode: boolean = false; // 新增：是否為簽核模式，控制隱藏某些UI元素
  @Input() readonly: boolean = false; // 新增：只讀模式
  @Input() isViewMode: boolean = false; // 新增：查看模式
  @Input() viewFormData: any = {}; // 新增：查看模式下要顯示的表單資料

  @Output() Showlist = new EventEmitter<string>();
  @Output() fillform = new EventEmitter<string>();
  @Output() formSaved = new EventEmitter<boolean>();
  @Output() configChanged = new EventEmitter<FormFieldConfig[]>();
  @Output() recordAdded = new EventEmitter<any>(); // 修改：傳遞新增的資料而不只是 boolean
  @Output() formSubmitted = new EventEmitter<any>(); // 新增：表單提交事件
  @Output() formCancelled = new EventEmitter<void>(); // 新增：表單取消事件
  @Output() dataCreated = new EventEmitter<any>(); // 新增：資料建立完成事件，傳遞新建立的資料
  @Output() formDraftSaved = new EventEmitter<any>(); // 新增：表單草稿儲存事件

  // 從登入資訊取得
  companyid = "";
  Username = "";
  userid = "";
  forminfo: any = {};

    // 引用資料（來自其他表單的預填資料）
  referenceData: any = {};

  formGroup!: FormGroup;
  formfinished = false;
  isSubmitting = false; // 新增：防止重複提交標誌

  // 新增標誌，追蹤是否已嘗試提交表單
  submitAttempted = false;

  // 標記是否為正式存檔（非暫存）
  isFormalSave = false;

  // 新增：暫存模式標記，用於更明確地區分操作模式
  isDraftMode = false;

  // 新增：表單資料緩存，避免過度頻繁的計算
  private _formDataCache: { [key: string]: any } = {};
  private _lastFormDataHash: string = '';
  formDataForAutoNumbering: { [key: string]: any } = {};

  // 新增變數，用於編輯 config 的 JSON 字串
  configJSON: string = '';

  showJsonEditor: boolean = false;

  // 段落分組相關屬性
  groupedSections: Array<{
    sectionHeader?: FormFieldConfig;
    fields: FormFieldConfig[];
    isExpanded: boolean;
    completedCount: number;
    totalCount: number;
  }> = [];

  sectionStates: { [key: string]: boolean } = {};

  initiatorInfo: InitiatorInfo = {
    userId: '',
    userName: '',
    departmentId: '',
    departmentName: '',
    departmentCode: ''
  };

  // 階段化表單相關屬性
  enableStageMode: boolean = false;
  formStages: FormStage[] = [];
  currentStageIndex: number = 0;
  currentStage: FormStage | null = null;

  // 表單名稱驗證相關屬性
  formNameError: string = '';
  isFormNameValid: boolean = false;

  constructor(
    private fb: FormBuilder,
    private databaseService: DatabaseService,
    private formConfigService: FormConfigService,
    private assistantUrlService: AssistantUrlService,
    private http: HttpClient,
    private authService: AuthService,
    private i18nService: I18nService,
    private emailService: EmailService,
    private departmentService: DepartmentService,
    private guidService: GuidService,
    private fieldRelationshipService: FieldRelationshipService
  ) {}

  ngOnInit(): void {

    // 如果有傳入的 initialValues，檢查 multi-checkbox 資料
    if (this.initialValues && this.initialValues.multi_checkbox_1) {
      console.log("[DEBUG] " + this.i18nService.translate('dynamic_form.console.multi_checkbox_data') + ":", JSON.stringify(this.initialValues.multi_checkbox_1, null, 2));
    }


    this.companyid = environment.companyid;
    this.Username = localStorage.getItem('username') || 'Unknown User';
    this.userid = localStorage.getItem('userid') || '';
    this.assistantid = this.assistantUrlService.getAssistantIdFromUrl();

    this.forminfo = {
      username: this.Username,
      assistantid: this.assistantid,
      assistantname: this.assistantname,
      componentname: this.formName,
      createAt: {
        type: 'datetime',
        value: Math.floor(Date.now() / 1000)
      }
    };

        // 檢查是否有 referencedata
        if (this.initialValues.referencedata) {
          this.referenceData = this.initialValues.referencedata;
          console.log(this.i18nService.translate('dynamic_form.console.reference_data_loaded') + ':', this.referenceData);
        } else {
          this.referenceData = {};
        }

    if (this.config && this.preview == true) {
      this.initializeForm();
    } else {
      // 透過 formName 與 companyid 取得表單配置
      this.formConfigService.getFormConfig(this.companyid,this.formid,this.formName)
      .subscribe(async config => {
        this.config = config;
        this.loadStageConfiguration();

        // 如果 initialValues 沒有值但 editFormId 有值，透過 API 取得資料
        if ((!this.initialValues || Object.keys(this.initialValues).length === 0) && this.editFormId) {

          //透過這個方法 取得 initialValues 的值，在透過initializeForm 顯示
          await this.loadFormDataForEdit();
        }

        // 方案 B：針對施工申請單設定日期限制
        this.applyWorkApplicationDateConstraints();

        await this.initializeForm();
      });
    }

    // 在預覽模式下初始化表單名稱驗證
    if (this.preview) {
      // 如果表單名稱為空，設定為預設值
      if (!this.formName) {
        this.formName = '';
      }
      // 執行初始驗證
      this.validateFormName();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 config 輸入發生變更時，在預覽模式下發送 configChanged 事件
    if (changes['config'] && !changes['config'].firstChange && this.preview) {
      const currentConfig = changes['config'].currentValue;
      if (currentConfig && Array.isArray(currentConfig)) {
        // 延遲發送事件，避免在初始化期間發送
        setTimeout(() => {
          this.configChanged.emit([...currentConfig]);
        }, 0);
      }
    }

    // 如果 config 變更，重新初始化表單
    if (changes['config'] && this.config) {
      this.initializeForm();
    }

    // 監聽 parentDateRange 的變化
    if (changes['parentDateRange']) {
      console.log('[DynamicForm] 🔍 parentDateRange 變更偵測:');
      console.log('  - firstChange:', changes['parentDateRange'].firstChange);
      console.log('  - previousValue:', changes['parentDateRange'].previousValue);
      console.log('  - currentValue:', changes['parentDateRange'].currentValue);
    }
  }

  // 檢查配置中簽核流程元件的數量
  private checkApprovalFlowCount(config: FormFieldConfig[]): number {
    return config.filter(field => field.type === 'approval-flow' || field.type === 'approval-personnel-selector').length;
  }

  // 自動移除多餘的簽核流程元件
  private limitApprovalFlowComponents(config: FormFieldConfig[]): FormFieldConfig[] {
    let approvalFlowFound = false;
    return config.filter(field => {
      if (field.type === 'approval-flow' || field.type === 'approval-personnel-selector') {
        if (approvalFlowFound) {
          console.warn(this.i18nService.translate('dynamic_form.console.remove_extra_approval_component') + ':', field);
          return false; // 移除多餘的簽核流程元件
        }
        approvalFlowFound = true;
      }
      return true;
    });
  }

  // 根據 config 建立 Reactive Form 並更新 configJSON
  async initializeForm(): Promise<void> {

    console.log(this.i18nService.translate('dynamic_form.console.initialize_form') + ":", this.config);

    // 檢查並限制簽核流程元件數量
    if (this.config && Array.isArray(this.config)) {
      const approvalFlowCount = this.checkApprovalFlowCount(this.config);
      if (approvalFlowCount > 1) {
        console.warn(this.i18nService.translate('dynamic_form.console.limit_approval_components', { count: approvalFlowCount }));
        this.config = this.limitApprovalFlowComponents(this.config);

        // 只在非預覽模式時顯示警告
        if (!this.preview) {
          setTimeout(() => {
            alert(`表單配置中包含 ${approvalFlowCount} 個簽核流程元件，已自動保留第一個並移除其他 ${approvalFlowCount - 1} 個，每個表單最多只能包含一個簽核流程元件。`);
          }, 100);
        }
      }
    }

    const group: { [key: string]: any } = {};
    this.config.forEach(field => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }

      // 為 file-display 元件設定初始值（如果有預設檔案）
      let initialValue: any = '';
      if (field.type === 'file-display' && field.files && field.files.length > 0) {
        // 不論是否為預覽模式，都要載入預設檔案以供顯示
        initialValue = field.files;
        console.log(this.i18nService.translate('dynamic_form.console.file_display_default') + ':', field.name, field.files);
      } else if (this.isViewMode && this.viewFormData && this.viewFormData.hasOwnProperty(field.name)) {
        // 在查看模式下使用 viewFormData 中的資料
        initialValue = this.viewFormData[field.name];
        console.log(this.i18nService.translate('dynamic_form.console.view_mode_field_value', { fieldName: field.name }) + ':', initialValue);
      } else if (this.initialValues && this.initialValues.hasOwnProperty(field.name)) {
        // 使用傳入的初始值
        initialValue = this.initialValues[field.name];
        console.log(this.i18nService.translate('dynamic_form.console.set_field_initial_value', { fieldName: field.name }) + ':', initialValue);

        // 特別處理簽名元件 - 確保資料結構正確
        if (field.type === 'signature' && initialValue) {
          // 如果資料已經是正確格式（有 imageData 屬性），就保持原樣
          // 否則可能需要額外處理
          if (!initialValue.imageData && typeof initialValue === 'string') {
            // 如果只是字串（舊格式），轉換為新格式
            initialValue = {
              imageData: initialValue,
              signerInfo: undefined
            };
          }
        }
      } else if ((field as any).initialValue !== undefined) {
        // 使用欄位配置中的初始值
        initialValue = (field as any).initialValue;
        console.log(this.i18nService.translate('dynamic_form.console.use_field_config_initial_value', { fieldName: field.name }) + ':', initialValue);
      }

      // 為 subform-relation 元件設定初始值（使用 formId）
      if (field.type === 'subform-relation' && field.formId) {
        initialValue = field.formId;
        console.log(this.i18nService.translate('dynamic_form.console.subform_relation') + ':', field.name, field.formId);
      }

      group[field.name] = [initialValue, validators];

      // 在只讀模式下禁用表單控件
      if (this.readonly || this.isViewMode) {
        // 將會在 FormGroup 建立後進行禁用
      }
    });
    this.formGroup = this.fb.group(group);

    console.log(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.form_init_completed')}，FormGroup ${this.i18nService.translate('dynamic_form.console.controls')}:`, Object.keys(this.formGroup.controls));

    // 處理 initialValues 中的攤平欄位（如 referencedata_* 欄位）
    if (this.initialValues && Object.keys(this.initialValues).length > 0) {
      const referencedataFields = Object.keys(this.initialValues).filter(key => key.startsWith('referencedata_'));

      if (referencedataFields.length > 0) {
        console.log(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.found_flattened_referencedata')}:`, referencedataFields);

        // 為每個攤平欄位建立 FormControl
        referencedataFields.forEach(fieldKey => {
          if (!this.formGroup.get(fieldKey)) {
            const fieldValue = this.initialValues[fieldKey];
            const control = this.fb.control(fieldValue);
            this.formGroup.addControl(fieldKey, control);
            console.log(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.added_flattened_field')}: ${fieldKey}`, fieldValue);
          }
        });

        console.log(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.updated_formgroup_controls')}:`, Object.keys(this.formGroup.controls));
      }
    }

    // 在只讀模式下禁用所有表單控件
    if (this.readonly || this.isViewMode) {
      this.formGroup.disable();
    }

    // 重置提交嘗試標誌，確保新表單不會立即顯示驗證錯誤
    this.submitAttempted = false;

    // 將 config 轉為 JSON 格式（縮排2個空格），供 textarea 編輯
    this.configJSON = JSON.stringify(this.config, null, 2);

    // 初始化段落分組
    this.initializeSections();

    // 訂閱表單值變化，用於更新完成狀態
    this.formGroup.valueChanges.subscribe(() => {
      this.updateSectionCompletionCounts();
      // 更新表單資料緩存，供自動編號元件使用
      this.updateFormDataCache();
    });

    // 初始化表單資料緩存
    this.updateFormDataCache();

    // 在預覽模式下，監聽檔案上傳變化並同步到配置
    if (this.preview) {
      this.formGroup.valueChanges.subscribe(formValues => {
        this.syncFileDataToConfig(formValues);
      });
    }

    if( this.getuseridlist) {
  // 新增：檢查是否有簽核流程元件並自動解析
    await this.checkAndResolveApprovalFlow();
    }

    // 確保特定欄位在編輯模式下正確顯示
    // 延遲執行以確保元件已完全初始化
    if (this.editMode && this.initialValues) {
      setTimeout(() => {
        this.config.forEach(field => {
          // 處理簽名欄位
          if (field.type === 'signature' && this.initialValues[field.name]) {
            const control = this.formGroup.get(field.name);
            if (control) {
              const signatureData = this.initialValues[field.name];
              // 強制更新值以觸發 ControlValueAccessor
              control.setValue(signatureData, { emitEvent: false });
              setTimeout(() => {
                control.updateValueAndValidity({ emitEvent: true });
              }, 100);
            }
          }
          // 處理位置欄位
          else if (field.type === 'location' && this.initialValues[field.name]) {
            const control = this.formGroup.get(field.name);
            if (control) {
              const locationData = this.initialValues[field.name];
              // 強制更新值以觸發 ControlValueAccessor
              control.setValue(locationData, { emitEvent: false });
              setTimeout(() => {
                control.updateValueAndValidity({ emitEvent: true });
              }, 100);
            }
          }
          // 處理單選按鈕欄位
          else if (field.type === 'radiobutton' && this.initialValues[field.name]) {
            const control = this.formGroup.get(field.name);
            if (control) {
              const radioData = this.initialValues[field.name];
              // 強制更新值以觸發 ControlValueAccessor
              control.setValue(radioData, { emitEvent: false });
              setTimeout(() => {
                control.updateValueAndValidity({ emitEvent: true });
              }, 100);
            }
          }
          // 處理多選核取方塊欄位
          else if (field.type === 'multi-checkbox' && this.initialValues[field.name]) {
            const control = this.formGroup.get(field.name);
            if (control) {
              const checkboxData = this.initialValues[field.name];
              // 強制更新值以觸發 ControlValueAccessor
              control.setValue(checkboxData, { emitEvent: false });
              setTimeout(() => {
                control.updateValueAndValidity({ emitEvent: true });
              }, 100);
            }
          }
          // 處理自動編號欄位
          else if (field.type === 'auto-numbering' && this.initialValues[field.name]) {
            const control = this.formGroup.get(field.name);
            if (control) {
              const autoNumberData = this.initialValues[field.name];
              // 強制更新值以觸發 ControlValueAccessor
              control.setValue(autoNumberData, { emitEvent: false });
              setTimeout(() => {
                control.updateValueAndValidity({ emitEvent: true });
              }, 100);
            }
          }
          // 處理必要文件上傳欄位
          else if (field.type === 'required-document-upload' && this.initialValues[field.name]) {
            const control = this.formGroup.get(field.name);
            if (control) {
              const documentData = this.initialValues[field.name];
              // 強制更新值以觸發 ControlValueAccessor
              control.setValue(documentData, { emitEvent: false });
              setTimeout(() => {
                control.updateValueAndValidity({ emitEvent: true });
              }, 100);
            }
          }
        });
      }, 500);
    }

    // ========================================
    // 新增：註冊欄位關係引擎
    // ========================================
    if (this.formid && this.formGroup) {
      console.log(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.prepare_field_relationship')}:`, {
        formid: this.formid,
        hasFormGroup: !!this.formGroup,
        formGroupControls: Object.keys(this.formGroup.controls)
      });

      setTimeout(() => {
        console.log(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.registering_field_relationship')}: ${this.formid}`);
        this.fieldRelationshipService.registerFormRelationships(this.formid, this.formGroup);
      }, 600); // 確保表單完全初始化後再註冊關係
    } else {
      console.warn(`[DynamicForm] ${this.i18nService.translate('dynamic_form.console.cannot_register_field_relationship')}:`, {
        formid: this.formid,
        hasFormGroup: !!this.formGroup
      });
    }
  }

  // 初始化段落分組
  initializeSections(): void {
    this.groupedSections = [];
    let currentSection: {
      sectionHeader?: FormFieldConfig;
      fields: FormFieldConfig[];
      isExpanded: boolean;
      completedCount: number;
      totalCount: number;
    } = {
      fields: [],
      isExpanded: true,
      completedCount: 0,
      totalCount: 0
    };

    this.config.forEach(field => {
      if (field.type === 'paragraph') {
        // 如果當前段落有欄位，先推入前一個段落
        if (currentSection.fields.length > 0 || currentSection.sectionHeader) {
          currentSection.totalCount = currentSection.fields.length;
          this.groupedSections.push(currentSection);
        }

        // 開始新段落
        currentSection = {
          sectionHeader: field,
          fields: [],
          isExpanded: this.sectionStates[field.id] !== undefined ? this.sectionStates[field.id] : true,
          completedCount: 0,
          totalCount: 0
        };
      } else {
        // 將非段落欄位加入當前段落
        currentSection.fields.push(field);
      }
    });

    // 推入最後一個段落
    if (currentSection.fields.length > 0 || currentSection.sectionHeader) {
      currentSection.totalCount = currentSection.fields.length;
      this.groupedSections.push(currentSection);
    }

    // 初始化完成狀態計算
    this.updateSectionCompletionCounts();
  }

  // 更新段落完成狀態計算
  updateSectionCompletionCounts(): void {
    if (!this.formGroup) return;

    this.groupedSections.forEach(section => {
      let completedCount = 0;
      section.fields.forEach(field => {
        const control = this.formGroup.get(field.name);
        if (control && this.isFieldCompleted(control.value, field)) {
          completedCount++;
        }
      });
      section.completedCount = completedCount;
    });
  }

  // 判斷欄位是否已完成
  isFieldCompleted(value: any, field: FormFieldConfig): boolean {
    if (value === null || value === undefined) return false;

    // 字串類型欄位
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    // 陣列類型欄位（如多選）
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // 其他類型
    return !!value;
  }

  // 處理段落展開/收合切換
  onSectionToggle(event: {sectionId: string, isExpanded: boolean}): void {
    this.sectionStates[event.sectionId] = event.isExpanded;

    // 更新對應段落的展開狀態
    const section = this.groupedSections.find(s => s.sectionHeader?.id === event.sectionId);
    if (section) {
      section.isExpanded = event.isExpanded;
    }
  }

  toggleJsonEditor(): void {
    this.showJsonEditor = !this.showJsonEditor;
  }

  // 解析 configJSON 並重新建立表單
  async reloadForm(): Promise<void> {
    try {
      const newConfig = JSON.parse(this.configJSON);
      this.config = newConfig;
      await this.initializeForm();

      // 發送配置變更事件，通知父元件同步更新
      this.configChanged.emit([...newConfig]);
    } catch (error) {
      console.error('Invalid JSON config:', error);
      alert('Invalid JSON format. Please check your config.');
    }
  }



  async onSubmit(): Promise<void> {
    // 防止重複提交檢查
    if (this.isSubmitting) {
      console.log(this.i18nService.translate('dynamic_form.console.submit_in_progress'));
      return;
    }

    // 設定提交中狀態
    this.isSubmitting = true;

    // 標記為已嘗試提交表單
    this.submitAttempted = true;

    // 設定標記，表示這是正式存檔（非暫存）
    this.isFormalSave = true;
    this.isDraftMode = false;

    // 檢查表單是否有效
    if (this.formGroup.invalid) {
      // 收集所有無效的必填欄位
      const invalidRequiredFields = this.getInvalidRequiredFields();

      if (invalidRequiredFields.length > 0) {
        const fieldNames = invalidRequiredFields.map(field => field.label).join('、');
        const errorMessage = this.i18nService.translate('dynamic_form.required_fields_missing', { fieldNames });

        // 顯示詳細錯誤訊息
        console.warn(this.i18nService.translate('dynamic_form.console.form_validation_failed') + ':', invalidRequiredFields);
        alert(errorMessage);

        // 滾動到第一個錯誤欄位
        this.scrollToFirstError();
      } else {
        console.warn(this.i18nService.translate('dynamic_form.console.form_validation_failed_check'));
      }

      // 標記所有欄位為已觸摸，以顯示驗證錯誤
      this.formGroup.markAllAsTouched();
      this.isFormalSave = false; // 重置標記
      this.isSubmitting = false; // 重置提交狀態
      return;
    }

    // 取得所有欄位的值
    const rawData = this.formGroup.value;

    // 在編輯模式下，且是正式存檔時，檢查簽核流程是否為空，如果為空則補充資料
    // 暫存模式不會執行此邏輯
    if (this.editMode && this.isFormalSave) {
      console.log(this.i18nService.translate('dynamic_form.console.formal_save_mode'));
      await this.checkAndFillEmptyApprovalFlow(rawData);
    } else if (this.editMode && !this.isFormalSave) {
      console.log(this.i18nService.translate('dynamic_form.console.temp_save_mode'));
    }

    // 根據 config 產生一個 mapping，每個欄位以 { type, value } 儲存
    const fieldsData: { [key: string]: { type: string; value: any } } = {};
    this.config.forEach(field => {
      fieldsData[field.name] = {
        type: field.type,
        value: rawData[field.name]
      };
    });

    // 轉換特定元件的 value 為 label
    this.transformValueToLabel(fieldsData);

    // 新增：檢查是否需要跳過前兩關簽核（⭐ 修正：移到轉換之後，使用已轉換的 fieldsData）
    const shouldSkipFirstTwoSteps = this.checkShouldSkipFirstTwoApprovalSteps(fieldsData);
    console.log('是否需要跳過前兩關簽核:', shouldSkipFirstTwoSteps);

    // 新增：如果需要跳過前兩關，調整簽核流程 steps
    if (shouldSkipFirstTwoSteps && this.isFormalSave) {
      console.log('✅ ' + this.i18nService.translate('dynamic_form.console.remove_first_two_steps') + '...');
      this.removeFirstTwoApprovalSteps(fieldsData);
    }

    console.log(this.i18nService.translate('dynamic_form.console.field_data_before_submit') + '：', fieldsData);

    console.log();

    // 檢查是否已有現有 ID (從 ExtraSaveDate、forminfo 或編輯模式的 editFormId)
    const existingId = this.editFormId;

    // When creating a new entry, retrieve department information
    if (!this.editMode && (!this.initiatorInfo.departmentId || !this.initiatorInfo.departmentName)) {
      await this.prepareInitiatorInfo();
      console.log(this.i18nService.translate('dynamic_form.console.department_info_prepared_for_new_record') + ':', this.initiatorInfo);
    }

    // 將 forminfo 與 fieldsData 合併，展開 fieldsData 為頂層屬性
    const dataToSave = {
      ...this.forminfo,
      ...this.ExtraSaveDate, // 先合併額外儲存的資料
      // 然後設定重要的核心欄位，確保不被 ExtraSaveDate 覆蓋
      formid: this.formid,
      parent_formid: this.parent_formid, // 確保 parent_formid 不被清空
      // 重要：無論編輯模式或新建模式，都必須包含正確的 ID
      id: existingId || this.guidService.generateGuid(),
      ...(this.editMode ? {
        updated_by: this.userid
      } : {
        created_by: this.userid,
        department_id: this.initiatorInfo.departmentId,
        department_name: this.initiatorInfo.departmentName,
        department_code: this.initiatorInfo?.departmentCode || ''
      }), // 根據編輯模式決定加入 createby 或 updateby
      ...fieldsData,
      // 明確標記為正式提交（非草稿）
      isDraft: false,
      submittedAt: new Date().toISOString()
    };

    // 🔍 DEBUG: 印出使用者 ID 相關資訊
    console.log('🔍 [onSubmit] 使用者 ID 資訊:', {
      'this.userid': this.userid,
      'localStorage.userid': localStorage.getItem('userid'),
      'editMode': this.editMode,
      'created_by (將儲存的值)': this.editMode ? '(編輯模式，不設定)' : this.userid,
      'dataToSave.created_by': dataToSave.created_by
    });

    console.log(this.i18nService.translate('dynamic_form.console.extra_save_date') + '：', this.ExtraSaveDate);
    console.log(this.i18nService.translate('dynamic_form.console.submit_data') + '：', dataToSave);
    console.log(this.i18nService.translate('dynamic_form.console.edit_mode_original_id') + '：', this.editMode, this.i18nService.translate('dynamic_form.console.original_id') + '：', existingId);

    console.log(this.i18nService.translate('dynamic_form.console.parent_formid_protection') + ':');
    console.log('- ' + this.i18nService.translate('dynamic_form.console.input_parent_formid') + ':', this.parent_formid);
    console.log('- ' + this.i18nService.translate('dynamic_form.console.extra_save_parent_formid') + ':', this.ExtraSaveDate?.parent_formid);
    console.log('- ' + this.i18nService.translate('dynamic_form.console.final_parent_formid') + ':', dataToSave.parent_formid);
    console.log(this.i18nService.translate('dynamic_form.console.id_source_analysis') + ':');
    console.log('- ExtraSaveDate.id:', this.ExtraSaveDate?.id);
    console.log('- forminfo.id:', this.forminfo?.id);
    console.log('- editFormId:', this.editFormId);
    console.log('- ' + this.i18nService.translate('dynamic_form.console.final_id_used') + ':', dataToSave.id);
    console.log('- companyid:', dataToSave.companyid);

    // 如果有訂閱 formSubmitted 事件，則發射事件而不是直接儲存
    if (this.formSubmitted.observed) {
      console.log(this.i18nService.translate('dynamic_form.console.emit_form_submitted'));
      this.formSubmitted.emit(rawData);
      return;
    }

    // 根據編輯模式選擇合適的 API
    const apiCall = this.editMode
      ? this.updateFormContainer(dataToSave)
      : this.addFormContainer(dataToSave);

    apiCall
      .then(response => {
        console.log('Response from backend:', response);
        this.formfinished = true;

        // 新增：檢查並發送第一關簽核通知（僅在正式存檔時）
        if (this.isFormalSave) {
          console.log(this.i18nService.translate('dynamic_form.console.formal_save_complete_notify'));
          this.checkAndSendFirstStageNotification(dataToSave);
        } else {
          console.log(this.i18nService.translate('dynamic_form.console.temp_save_complete_skip_notify'));
        }

        // 發射記錄已新增事件，通知父組件更新列表
        this.recordAdded.emit(true);

        // 新增：發射資料建立完成事件，傳遞新建立的資料
        this.dataCreated.emit(dataToSave);
      })
      .catch(error => {
        console.error('Error submitting data:', error);
      })
      .finally(() => {
        // 重置所有處理標記
        this.isFormalSave = false;
        this.isDraftMode = false;
        this.isSubmitting = false;
      });
  }

  /**
   * 新增表單容器資料
   */
  private async addFormContainer(data: any): Promise<any> {
    const apiUrl = `${environment.apiUrl}/api/form-manager/add_form_container`;
    return this.http.post(apiUrl, data).toPromise();
  }

  /**
   * 更新表單容器資料
   */
  private async updateFormContainer(data: any): Promise<any> {
    const apiUrl = `${environment.apiUrl}/api/form-manager/update_form_container`;
    return this.http.put(apiUrl, data).toPromise();
  }

  /**
   * 暫存表單資料 - 跳過驗證直接儲存
   */
  onDraftSave(): void {
    // 防止重複提交檢查
    if (this.isSubmitting) {
      console.log(this.i18nService.translate('dynamic_form.console.temp_save_in_progress'));
      return;
    }

    this.isSubmitting = true;

    // 明確設定這不是正式存檔，而是暫存模式
    this.isFormalSave = false;
    this.isDraftMode = true;

    // 取得所有欄位的值，不進行驗證
    const rawData = this.formGroup.value;

    // 根據 config 產生一個 mapping，每個欄位以 { type, value } 儲存
    const fieldsData: { [key: string]: { type: string; value: any } } = {};
    this.config.forEach(field => {
      fieldsData[field.name] = {
        type: field.type,
        value: rawData[field.name]
      };
    });

    // 轉換特定元件的 value 為 label
    this.transformValueToLabel(fieldsData);

    // 暫存模式：過濾掉簽核流程相關欄位
    const filteredFieldsData = this.filterApprovalFieldsForDraft(fieldsData);

    // 檢查是否已有現有 ID (從 ExtraSaveDate、forminfo 或編輯模式的 editFormId)
    const existingId = this.editFormId;

    console.log("existingId",existingId);

    // 將 forminfo 與 fieldsData 合併，展開 fieldsData 為頂層屬性
    const dataToSave = {
      ...this.forminfo,
      ...this.ExtraSaveDate,  // 先合併 ExtraSaveDate
      // 然後設定重要的核心欄位，確保不被 ExtraSaveDate 覆蓋
      formid: this.formid,
      parent_formid: this.parent_formid, // 確保 parent_formid 不被清空
      // 重要：無論編輯模式或新建模式，都必須包含正確的 ID
      id: existingId || this.guidService.generateGuid(),
      ...(this.editMode ? {
        updated_by: this.userid
      } : {
        created_by: this.userid,
        departmentId: this.initiatorInfo.departmentId,
        departmentName: this.initiatorInfo.departmentName
      }),
      ...filteredFieldsData, // 使用過濾後的欄位資料
      // 關鍵：新增草稿狀態標記
      isDraft: true,
      draftSavedAt: new Date().toISOString()
    };

    console.log(this.i18nService.translate('dynamic_form.console.temp_save_data') + '：', dataToSave);
    console.log(this.i18nService.translate('dynamic_form.console.edit_mode_original_id') + '：', this.editMode, this.i18nService.translate('dynamic_form.console.original_id') + '：', existingId);
    console.log(this.i18nService.translate('dynamic_form.console.parent_formid_protection') + ':');
    console.log('- ' + this.i18nService.translate('dynamic_form.console.input_parent_formid') + ':', this.parent_formid);
    console.log('- ' + this.i18nService.translate('dynamic_form.console.extra_save_parent_formid') + ':', this.ExtraSaveDate?.parent_formid);
    console.log('- ' + this.i18nService.translate('dynamic_form.console.final_parent_formid') + ':', dataToSave.parent_formid);
    console.log(this.i18nService.translate('dynamic_form.console.id_source_analysis') + ':');
    console.log('- ExtraSaveDate.id:', this.ExtraSaveDate?.id);
    console.log('- forminfo.id:', this.forminfo?.id);
    console.log('- editFormId:', this.editFormId);
    console.log('- ' + this.i18nService.translate('dynamic_form.console.final_id_used') + ':', dataToSave.id);
    console.log('- companyid:', dataToSave.companyid);

    // 如果有訂閱 formDraftSaved 事件，則發射事件而不是直接儲存
    if (this.formDraftSaved.observed) {
      console.log(this.i18nService.translate('dynamic_form.console.emit_form_draft_saved'));
      this.formDraftSaved.emit(rawData);
      this.isSubmitting = false; // 重置狀態
      return;
    }

    // 根據編輯模式選擇合適的 API
    const apiCall = this.editMode
      ? this.updateFormContainer(dataToSave)
      : this.addFormContainer(dataToSave);

    apiCall
      .then(response => {
        console.log(this.i18nService.translate('dynamic_form.console.draft_save_success') + ':', response);

        // 重要：保存 ID 到 ExtraSaveDate，確保後續暫存使用同一個 ID
        if (dataToSave.id && !this.ExtraSaveDate?.id) {
          this.ExtraSaveDate = { ...this.ExtraSaveDate, id: dataToSave.id };
          console.log(this.i18nService.translate('dynamic_form.console.saved_id_to_extra_save_date') + ':', dataToSave.id);
        }

        // 顯示草稿儲存成功訊息
        const successMessage = this.i18nService.translate('form.draft.save_success');
        alert(successMessage);
        // 不設定 formfinished = true，讓使用者可以繼續編輯
        // 暫存模式：明確不呼叫 checkAndSendFirstStageNotification()
        // 確保 isFormalSave 標記為 false，避免任何簽核流程觸發
        console.log(this.i18nService.translate('dynamic_form.console.temp_save_success_no_approval'));

        // 發射記錄已新增事件，但標記為草稿
        this.recordAdded.emit({ isDraft: true, data: dataToSave });
      })
      .catch(error => {
        console.error(this.i18nService.translate('dynamic_form.console.draft_save_failed') + ':', error);
        const errorMessage = this.i18nService.translate('form.draft.save_failed');
        alert(errorMessage);
      })
      .finally(() => {
        // 重置所有處理標記
        this.isFormalSave = false;
        this.isDraftMode = false;
        this.isSubmitting = false; // 確保無論成功或失敗都重置狀態
      });
  }

  // 取得無效的必填欄位
  getInvalidRequiredFields(): FormFieldConfig[] {
    const invalidFields: FormFieldConfig[] = [];

    this.config.forEach(field => {
      if (field.required) {
        const control = this.formGroup.get(field.name);
        if (control && control.invalid) {
          invalidFields.push(field);
        }
      }
    });

    return invalidFields;
  }

  // 取得無效必填欄位的標籤文字，用於顯示錯誤訊息
  getInvalidRequiredFieldsText(): string {
    const invalidFields = this.getInvalidRequiredFields();
    return invalidFields.map(field => field.label).join('、');
  }

  // 滾動到第一個錯誤欄位
  scrollToFirstError(): void {
    const firstInvalidField = this.getInvalidRequiredFields()[0];
    if (firstInvalidField) {
      // 使用 setTimeout 確保 DOM 已更新
      setTimeout(() => {
        const element = document.querySelector(`[data-field-name="${firstInvalidField.name}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  /**
   * 暫存模式：過濾掉簽核流程相關欄位
   * 避免在暫存時儲存簽核流程資料
   */
  private filterApprovalFieldsForDraft(fieldsData: { [key: string]: { type: string; value: any } }): { [key: string]: { type: string; value: any } } {
    const filteredData = { ...fieldsData };

    // 識別需要過濾的簽核相關欄位類型
    const approvalFieldTypes = ['approval-flow', 'approval-personnel-selector'];

    // 過濾掉簽核相關欄位
    Object.keys(filteredData).forEach(fieldName => {
      const fieldData = filteredData[fieldName];
      if (fieldData && approvalFieldTypes.includes(fieldData.type)) {
        console.log(`${this.i18nService.translate('dynamic_form.console.temp_mode_filter_approval_field')} ${fieldName} (type: ${fieldData.type})`);
        // 選項1：完全移除該欄位
        delete filteredData[fieldName];

        // 選項2：保留欄位但清空值（如果需要保持欄位結構）
        // filteredData[fieldName] = {
        //   type: fieldData.type,
        //   value: null
        // };
      }
    });

    console.log(this.i18nService.translate('dynamic_form.console.temp_mode_approval_filter_complete'), {
      原始欄位數: Object.keys(fieldsData).length,
      過濾後欄位數: Object.keys(filteredData).length,
      被過濾的欄位: Object.keys(fieldsData).filter(key => !filteredData[key])
    });

    return filteredData;
  }

  // 檢查特定欄位是否無效（用於模板）
  isFieldInvalid(field: FormFieldConfig): boolean {
    if (!field.required || !this.formGroup) return false;

    const control = this.formGroup.get(field.name);
    return !!(control && control.invalid && (control.dirty || control.touched || this.submitAttempted));
  }

  // 轉換特定元件的儲存值從 value 改為 label 或保持完整資料結構
  private transformValueToLabel(fieldsData: { [key: string]: { type: string; value: any } }): void {
    console.log(this.i18nService.translate('dynamic_form.console.start_value_to_label_transform') + '...');

    console.log(this.i18nService.translate('dynamic_form.console.original_field_data') + ':', fieldsData);

    this.config.forEach(field => {
      const fieldData = fieldsData[field.name];
      if (!fieldData) return;

      const originalValue = JSON.stringify(fieldData.value);

      switch (field.type) {
        case 'multi-checkbox':
          if (fieldData.value) {
            fieldData.value = this.transformMultiCheckboxValue(field, fieldData.value);
          }
          break;
        case 'radiobutton':
          console.log(`${this.i18nService.translate('dynamic_form.console.handling_radiobutton_field')}: ${field.name}`);
          console.log(`${this.i18nService.translate('dynamic_form.console.original_value')}:`, fieldData.value);
          if (fieldData.value) {
            fieldData.value = this.transformRadiobuttonValue(field, fieldData.value);
            console.log(`${this.i18nService.translate('dynamic_form.console.converted_value')}:`, fieldData.value);
          }
          break;
        case 'department-selector':
          fieldData.value = this.transformDepartmentValue(field, fieldData.value);
          break;
        case 'select':
          if (fieldData.value) {
            fieldData.value = this.transformSelectValue(field, fieldData.value);
          }
          break;
        case 'personnel-selector':
          fieldData.value = this.transformPersonnelValue(field, fieldData.value);
          break;
        case 'dynamic-api-select':
          // 將整個 fieldData 替換為轉換後的結果，避免雙重巢狀
          const transformedApiSelect = this.transformDynamicApiSelectValue(field, fieldData.value);
          fieldData.type = transformedApiSelect.type;
          fieldData.value = transformedApiSelect.value;
          break;
        case 'approval-flow':
        case 'approval-personnel-selector':
          // 關鍵修復：簽核流程資料需要保持完整結構
          fieldData.value = this.transformApprovalFlowValue(field, fieldData.value);
          break;
        case 'file-upload-manager':
          fieldData.value = this.transformFileUploadManagerValue(field, fieldData.value);
          break;
        case 'required-document-upload':
          fieldData.value = this.transformRequiredDocumentUploadValue(field, fieldData.value);
          break;
        case 'avatar-upload':
          fieldData.value = this.transformAvatarUploadValue(field, fieldData.value);
          break;
        case 'signature':
          fieldData.value = this.transformSignatureValue(field, fieldData.value);
          break;
        case 'meeting-signature-collection':
          fieldData.value = this.transformMeetingSignatureCollectionValue(field, fieldData.value);
          break;
        case 'dynamic-api-table':
          // 將整個 fieldData 替換為轉換後的結果，避免雙重巢狀
          const transformedApiTable = this.transformDynamicApiTableValue(field, fieldData.value);
          fieldData.type = transformedApiTable.type;
          fieldData.value = transformedApiTable.value;
          break;
        case 'auto-numbering':
          fieldData.value = this.transformAutoNumberingValue(field, fieldData.value);
          break;
        case 'risk-assessment-matrix':
          fieldData.value = this.transformRiskAssessmentMatrixValue(field, fieldData.value);
          break;
        case 'audio-recorder':
          fieldData.value = this.transformAudioRecorderValue(field, fieldData.value);
          break;
        case 'video-player':
          fieldData.value = this.transformVideoPlayerValue(field, fieldData.value);
          break;
        case 'fill-in':
          fieldData.value = this.transformFillInFieldValue(field, fieldData.value);
          break;
        case 'subform-relation':
          fieldData.value = this.transformFormSubformRelationValue(field, fieldData.value);
          break;
        case 'tags':
          fieldData.value = this.transformTagsValue(field, fieldData.value);
          break;
        case 'location':
          fieldData.value = this.transformLocationValue(field, fieldData.value);
          break;
        case 'timestamp':
          fieldData.value = this.transformTimestampValue(field, fieldData.value);
          break;
        // 其他元件類型保持原值
        default:
          break;
      }

      const newValue = JSON.stringify(fieldData.value);
      if (originalValue !== newValue) {
        console.log(`${field.type} ${this.i18nService.translate('dynamic_form.console.field_converted')} "${field.name}":`, {
          原值: originalValue,
          新值: newValue
        });
      }
    });

    console.log(this.i18nService.translate('dynamic_form.console.form_value_conversion_complete'));
  }

  // 轉換 multi-checkbox 的值
  private transformMultiCheckboxValue(field: FormFieldConfig, value: any): any {
    if (!Array.isArray(value) || !field.options) return value;

    // 處理 CheckboxValue[] 格式
    if (value.length > 0 && value[0].hasOwnProperty('option') && value[0].hasOwnProperty('selected')) {
      const result = value.map((item: any) => {
        // 在 field.options 中尋找對應的 label
        const option = field.options?.find((opt: any) => opt.value === item.option);

        // 將 option 改為 key/value 結構，與 select/radiobutton 保持一致
        // 重要：保留 textValue 欄位
        return {
          ...item,  // 這會保留 selected 和 textValue
          option: option ? {
            key: item.option,    // 原始值（例如: "option1"）
            value: option.label  // 顯示標籤（例如: "選項一"）
          } : {
            key: item.option,
            value: item.option   // 如果找不到對應選項，key 和 value 都使用原始值
          },
          // 明確保留 textValue（如果存在）
          textValue: item.textValue || ''
        };
      });
      return result;
    }

    // 處理 Category[] 格式
    if (value.length > 0 && value[0].hasOwnProperty('name') && value[0].hasOwnProperty('selected')) {
      // 這種格式已經使用 name (label)，但為了一致性，也改為 key/value 結構
      return value.map((item: any) => {
        // 如果有對應的 value，使用它作為 key；否則使用 name 作為 key
        const originalKey = item.value || item.name;
        return {
          ...item,
          name: {
            key: originalKey,
            value: item.name   // name 本身就是顯示標籤
          },
          value: originalKey  // 保持 value 欄位用於其他邏輯
        };
      });
    }

    return value;
  }

  // 轉換 radiobutton 的值
  private transformRadiobuttonValue(field: FormFieldConfig, value: any): any {
    console.log(`${this.i18nService.translate('dynamic_form.console.radiobutton_field_input')}: ${field.name}, ${this.i18nService.translate('dynamic_form.console.original_value')}:`, value);
    console.log(`${this.i18nService.translate('dynamic_form.console.radiobutton_options')}:`, field.options);

    if (!field.options) {
      console.log(`${this.i18nService.translate('dynamic_form.console.no_options_return_original')}`);
      return value;
    }

    // 處理簡單字串值
    if (typeof value === 'string') {
      const option = field.options?.find((opt: any) => opt.value === value);
      console.log(`${this.i18nService.translate('dynamic_form.console.found_option')}:`, option);

      // 返回包含 key 和 value 的物件結構，與 select 保持一致
      const result = option ? {
        key: value,        // 原始值（例如: "option1"）
        value: option.label // 顯示標籤（例如: "是"）
      } : {
        key: value,
        value: value       // 如果找不到對應選項，key 和 value 都使用原始值
      };

      console.log(`${this.i18nService.translate('dynamic_form.console.conversion_result')}:`, result);
      return result;
    }

    // 處理 RadioButtonValue 格式（複雜物件，包含備註、檔案等）
    if (value && typeof value === 'object' && value.hasOwnProperty('selectedValue')) {
      console.log(`${this.i18nService.translate('dynamic_form.console.detected_complete_radiobutton_object')}`);

      // 對於複雜的 RadioButtonValue 物件，**完全保留原始結構**
      // 這樣可以保留 conditionalData 中的圖片、錄音、備註等資料
      const result = { ...value };

      // 只轉換 selectedValue 中的標籤顯示
      if (typeof value.selectedValue === 'string') {
        const option = field.options?.find((opt: any) => opt.value === value.selectedValue);
        console.log(`${this.i18nService.translate('dynamic_form.console.object_format_found_option')}:`, option);

        result.selectedValue = option ? {
          key: value.selectedValue,  // 原始值
          value: option.label        // 顯示標籤
        } : {
          key: value.selectedValue,
          value: value.selectedValue
        };
      }

      console.log(`${this.i18nService.translate('dynamic_form.console.complete_object_conversion_result')}:`, result);
      return result;
    }

    console.log(`${this.i18nService.translate('dynamic_form.console.no_conditions_return_original')}:`, value);
    return value;
  }

  // 轉換 department-selector 的值
  private transformDepartmentValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return {
        key: '',
        value: ''
      };
    }

    // department-selector 組件傳遞簡單的 ID 值
    if (typeof value === 'string') {
      // 嘗試從 departmentService 獲取部門名稱
      const department = this.departmentService.getDepartmentById(value);

      return {
        key: value,                                    // 原始部門 ID
        value: department ? department.name : value    // 部門顯示名稱，如果找不到就用 ID
      };
    }

    // 處理已經是 key/value 格式的情況
    if (typeof value === 'object' && value.hasOwnProperty('key')) {
      // 如果 value 和 key 相同，表示需要轉換為部門名
      if (value.key && value.value === value.key) {
        const department = this.departmentService.getDepartmentById(value.key);
        return {
          key: value.key,
          value: department ? department.name : value.key
        };
      }

      // 已經是正確格式，直接返回
      return {
        key: value.key,
        value: value.value
      };
    }

    // 處理舊的複雜格式（向後相容）
    if (typeof value === 'object' && value.hasOwnProperty('type')) {
      // 提取 ID
      const departmentId = value.id || (value.value && value.value.id) ||
                          (value.value && value.value.key) || value.value;

      if (departmentId) {
        const department = this.departmentService.getDepartmentById(departmentId);
        return {
          key: departmentId,
          value: department ? department.name : departmentId
        };
      }
    }

    // 默認情況
    return {
      key: value,
      value: value
    };
  }

  // 轉換 select 的值
  private transformSelectValue(field: FormFieldConfig, value: any): any {
    if (!field.options) return value;

    const option = field.options?.find((opt: any) => opt.value === value);

    // 返回包含 key 和 value 的物件結構
    // key: 原始選擇的 value，用於邏輯處理
    // value: 對應的 label，用於前端顯示
    return option ? {
      key: value,        // 原始值（例如: "high"）
      value: option.label // 顯示標籤（例如: "高優先級"）
    } : {
      key: value,
      value: value       // 如果找不到對應選項，key 和 value 都使用原始值
    };
  }

  // 轉換 personnel-selector 的值
  private transformPersonnelValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return {
        key: '',
        value: ''
      };
    }

    // 單選模式 - 處理字串 ID
    if (typeof value === 'string') {
      const userName = this.getPersonnelNameById(value);
      return {
        key: value,        // 原始 ID
        value: userName    // 顯示名稱
      };
    }

    // 多選模式 - 處理 ID 陣列
    if (Array.isArray(value)) {
      return value.map(id => ({
        key: id,
        value: this.getPersonnelNameById(id)
      })).filter(user => user.value);
    }

    // 處理已經是 key/value 格式的情況
    if (typeof value === 'object' && value.hasOwnProperty('key')) {
      // 如果 value 和 key 相同，表示需要轉換為人員姓名
      if (value.key && value.value === value.key) {
        const userName = this.getPersonnelNameById(value.key);
        return {
          key: value.key,
          value: userName
        };
      }

      // 已經是正確格式，直接返回
      return {
        key: value.key,
        value: value.value
      };
    }

    // 處理舊的複雜格式（向後相容）
    if (typeof value === 'object' && value.hasOwnProperty('type')) {
      // 單選情況
      if (!Array.isArray(value.value)) {
        const personnelId = value.id || (value.value && value.value.id) ||
                           (value.value && value.value.key) || value.value;

        if (personnelId) {
          const userName = this.getPersonnelNameById(personnelId);
          return {
            key: personnelId,
            value: userName
          };
        }
      } else {
        // 多選情況 - 處理陣列
        return value.value.map((item: any) => {
          const id = item.key || item.id || item;
          return {
            key: id,
            value: this.getPersonnelNameById(id)
          };
        }).filter((user: any) => user.value);
      }
    }

    // 默認情況
    return {
      key: value,
      value: this.getPersonnelNameById(value)
    };
  }

  // 透過 ID 取得人員姓名
  private getPersonnelNameById(id: string): string {
    const user = this.departmentService.getUserById(id);
    return user ? user.name : id;
  }

  // 轉換 approval-flow 和 approval-personnel-selector 的值（關鍵修復）
  private transformApprovalFlowValue(field: FormFieldConfig, value: any): any {
    // 如果值為空，保持空值
    if (!value) {
      return value;
    }

    // 如果已經是完整的簽核流程資料結構，直接保持
    if (typeof value === 'object' && (value.type === 'approval-flow' || value.type === 'approval-personnel-selector') && value.value) {
      console.log(this.i18nService.translate('dynamic_form.console.approval_data_complete_format') + ':', value);
      return value;
    }

    // 如果是簽核流程物件但沒有 type 包裝，進行包裝
    if (typeof value === 'object' && (value.flowId || value.steps)) {
      console.log(this.i18nService.translate('dynamic_form.console.approval_data_needs_wrapping') + ':', value);
      return {
        type: 'approval-flow',
        value: value
      };
    }

    // 如果是字串 ID，嘗試從已載入的流程定義中獲取完整資料
    if (typeof value === 'string' && value) {
      console.log(this.i18nService.translate('dynamic_form.console.approval_data_string_conversion') + ':', value);
      // 這裡可以嘗試從快取或 API 獲取完整資料
      // 暫時保持原值，但加上 type 包裝
      return {
        type: 'approval-flow',
        value: value
      };
    }

    // 其他情況保持原值
    console.log(this.i18nService.translate('dynamic_form.console.approval_data_keep_original') + ':', value);
    return value;
  }

  // 轉換 dynamic-api-select 的值
  private transformDynamicApiSelectValue(field: FormFieldConfig, value: any): any {
    // 編輯模式下，如果值未被修改（仍然是字串ID），保持原有的資料結構
    if (this.editMode && typeof value === 'string' && this.initialValues && this.initialValues[field.name] === value) {
      // 從 ExtraSaveDate 或原始資料中取得完整結構
      const originalData = this.ExtraSaveDate[field.name];
      if (originalData && originalData.type === 'dynamic-api-select') {
        return originalData;
      }
    }

    if (!value) {
      return {
        type: 'dynamic-api-select',
        value: {
          key: '',
          value: ''
        }
      };
    }

    // 處理從 form-dynamic-api-select 元件傳來的格式（表示已編輯）
    if (typeof value === 'object' && value.type === 'form-container-select') {
      return {
        type: 'dynamic-api-select',
        value: {
          key: value.id || '',
          value: value.label || value.value || ''
        }
      };
    }

    // 如果已經是包含 key/value 的物件格式
    if (typeof value === 'object' && value.key !== undefined && value.value !== undefined) {
      return {
        type: 'dynamic-api-select',
        value: {
          key: value.key,
          value: value.value
        }
      };
    }

    // 如果是字串（只有 ID），需要查找對應的標籤
    if (typeof value === 'string') {
      // 嘗試從表單中的對應元件獲取已載入的選項資料
      const displayLabel = this.getDynamicApiSelectLabel(field, value);

      return {
        type: 'dynamic-api-select',
        value: {
          key: value,                    // 原始 ID
          value: displayLabel || value   // 顯示標籤，如果找不到則使用 ID
        }
      };
    }

    // 其他情況，保持簡化格式
    return {
      type: 'dynamic-api-select',
      value: {
        key: value || '',
        value: value || ''
      }
    };
  }

  // 嘗試從動態API選擇器組件中獲取選項標籤
  private getDynamicApiSelectLabel(field: FormFieldConfig, selectedValue: string): string | null {
    try {
      // 嘗試從DOM中找到對應的select元素並獲取選中選項的文字
      const selectElement = document.querySelector(`select[name="${field.name}"]`) as HTMLSelectElement;
      if (selectElement) {
        const selectedOption = selectElement.querySelector(`option[value="${selectedValue}"]`) as HTMLOptionElement;
        if (selectedOption && selectedOption.textContent) {
          return selectedOption.textContent.trim();
        }
      }
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.cannot_get_dynamic_api_selector_label') + ':', error);
    }

    return null;
  }

  // 轉換 file-upload-manager 的值
  private transformFileUploadManagerValue(field: FormFieldConfig, value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    try {
      // 處理文件管理器資料結構
      if (value.files && Array.isArray(value.files)) {
        const files = value.files;
        const fileCount = files.length;
        const totalSize = files.reduce((sum: number, file: any) => sum + (file.size || 0), 0);

        return {
          type: 'file-upload-manager',
          summary: `${fileCount} 個檔案 (${this.formatFileSize(totalSize)})`,
          fileCount: fileCount,
          totalSize: totalSize,
          files: files, // 保留完整的檔案資料，包含所有關鍵欄位
          originalValue: value
        };
      }

      // 處理其他格式
      return {
        type: 'file-upload-manager',
        summary: '檔案管理資料',
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.file_manager_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 required-document-upload 的值
  private transformRequiredDocumentUploadValue(field: FormFieldConfig, value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    // 檢查是否已經是轉換後的格式，避免重複巢狀
    if (value.type === 'required-document-upload' && value.summary !== undefined) {
      return value;
    }

    try {
      // 尋找實際的檔案資料（支援多層巢狀）
      let actualFileData = null;
      let actualAiResult = null;
      let current = value;
      let depth = 0;
      const maxDepth = 5;

      // 遞迴尋找最底層的實際資料
      while (current && depth < maxDepth) {
        if (current.file && !current.originalValue) {
          // 找到實際的檔案資料
          actualFileData = current.file;
          actualAiResult = current.aiParseResult;
          break;
        }
        if (current.originalValue) {
          current = current.originalValue;
        } else {
          break;
        }
        depth++;
      }

      let summary = '無文件';
      let aiResult = null;
      let fileName = '';

      // 處理文件資料
      if (actualFileData) {
        fileName = actualFileData.name || actualFileData.originalName || '';
        summary = `已上傳: ${fileName}`;
      } else if (value.file) {
        // 如果沒找到巢狀的檔案，使用頂層的檔案
        actualFileData = value.file;
        fileName = value.file.name || value.file.originalName || '';
        summary = `已上傳: ${fileName}`;
      }

      // 處理 AI 解析結果
      const aiParseResult = actualAiResult || value.aiParseResult;
      if (aiParseResult) {
        aiResult = {
          status: aiParseResult.status,
          confidence: aiParseResult.confidence,
          response: aiParseResult.response || aiParseResult.AI_Response,
          parsedAt: aiParseResult.parsedAt
        };

        if (aiParseResult.status === 'completed') {
          summary += ` (AI已解析)`;
        }
      }

      // 只保留實際的資料，不再巢狀
      const transformedValue: any = {
        type: 'required-document-upload',
        summary: summary,
        fileName: fileName,
        hasFile: !!actualFileData,
        aiParseResult: aiResult
      };

      // 只有在有實際檔案資料時才加入 originalValue
      if (actualFileData) {
        transformedValue.originalValue = {
          file: actualFileData,
          aiParseResult: aiParseResult,
          lastUpdated: new Date().toISOString()
        };
      }

      return transformedValue;
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.required_file_upload_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 avatar-upload 的值
  private transformAvatarUploadValue(field: FormFieldConfig, value: any): any {
    console.log(`${this.i18nService.translate('dynamic_form.console.avatar_upload_original_input')}:`, value);

    if (!value || typeof value !== 'object') {
      console.log(`${this.i18nService.translate('dynamic_form.console.avatar_upload_null_or_non_object')}:`, value);
      return value;
    }

    try {
      // 檢查是否是新的包裝格式（有 originalValue）
      if (value.originalValue && typeof value.originalValue === 'object') {
        console.log(`${this.i18nService.translate('dynamic_form.console.avatar_upload_detected_new_format')}:`, value.originalValue);
        // 返回舊格式（簡潔格式）
        return value.originalValue;
      }

      // 檢查是否已經是舊格式（有基本的檔案屬性）
      if (value.fileName && (value.fileUrl || value.blobName)) {
        console.log(`${this.i18nService.translate('dynamic_form.console.avatar_upload_already_old_format')}:`, value);
        // 已經是舊格式，直接返回
        return value;
      }

      // 如果都不是，嘗試從當前值提取基本資訊（保持向下相容）
      const fileName = value.fileName || '';
      const fileSize = value.fileSize || 0;
      const displayMode = value.displayMode || 'square';
      const fileUrl = value.fileUrl || value.blobName || '';
      const uploadedAt = value.uploadedAt || new Date().toISOString();
      const blobName = value.blobName || value.fileUrl || '';
      const previewUrl = value.previewUrl || '';

      const result = {
        fileName: fileName,
        fileUrl: fileUrl,
        fileSize: fileSize,
        uploadedAt: uploadedAt,
        displayMode: displayMode,
        blobName: blobName,
        previewUrl: previewUrl
      };

      console.log(`${this.i18nService.translate('dynamic_form.console.avatar_upload_generated_old_format')}:`, result);
      return result;

    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.avatar_upload_conversion_error') + ':', error);
      return value;
    }
  }

  /**
   * 轉換 signature 值 (簽名欄位)
   */
  private transformSignatureValue(field: FormFieldConfig, value: any): any {

    if (!value || typeof value !== 'object') return value;

    try {
      // 檢查是否已經是轉換後的格式
      if (value.type === 'signature' && value.hasOwnProperty('originalValue')) {
        return value;
      }

      // 處理簽名的資料結構 - 實際資料結構包含 imageData
      // 支援多種可能的資料結構
      let hasSignature = false;
      let signatureData = value;

      // 檢查不同的資料結構
      if (value.imageData && value.imageData.length > 0) {
        // 直接有 imageData
        hasSignature = true;
        signatureData = value;
      } else if (value.originalValue && value.originalValue.imageData) {
        // imageData 在 originalValue 中
        hasSignature = value.originalValue.imageData.length > 0;
        signatureData = value.originalValue;
      } else if (value.value && value.value.imageData) {
        // imageData 在 value 中
        hasSignature = value.value.imageData.length > 0;
        signatureData = value.value;
      }


      return {
        type: 'signature',
        summary: hasSignature ? this.i18nService.translate('form.approval.signed') : this.i18nService.translate('form.approval.unsigned'),
        hasSignature: hasSignature,
        value: signatureData,  // 使用 value 而不是 originalValue
        originalValue: signatureData  // 同時保留 originalValue 以相容舊代碼
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.signature_field_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 meeting-signature-collection 的值
  private transformMeetingSignatureCollectionValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return value;
    }

    try {
      let parsedValue = value;

      // 如果是字串，嘗試解析 JSON（向後相容）
      if (typeof value === 'string') {
        try {
          parsedValue = JSON.parse(value);
        } catch (e) {
          console.warn(this.i18nService.translate('dynamic_form.console.cannot_parse_meeting_signature_json') + ':', e);
          return value;
        }
      }

      // 處理物件格式的資料
      if (parsedValue && typeof parsedValue === 'object') {
        const participants = parsedValue.participants || [];

        if (Array.isArray(participants)) {
          const signedCount = participants.filter((p: any) => p.signature).length;
          const totalCount = participants.length;

          return {
            type: 'meeting-signature-collection',
            summary: `${this.i18nService.translate('form.approval.signature_collection')}: ${signedCount}/${totalCount} ${this.i18nService.translate('form.approval.signed')}`,
            signedCount: signedCount,
            totalCount: totalCount,
            participants: participants.map((p: any) => ({
              id: p.id,                               // 保留參與者 ID
              name: p.name,                           // 參與者姓名
              title: p.title,                         // 職稱
              department: p.department,               // 部門
              hasSigned: !!p.signature,               // 是否已簽名（布林值）
              signature: p.signature || null,         // 直接包含簽名資料
              signedAt: p.signedAt || null            // 簽名時間
            }))
            // 移除 originalValue 欄位
          };
        }
      }

      return {
        type: 'meeting-signature-collection',
        summary: '會議簽名收集',
        signedCount: 0,
        totalCount: 0,
        participants: []
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.meeting_signature_collection_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 dynamic-api-table 的值
  private transformDynamicApiTableValue(field: FormFieldConfig, value: any): any {
    // 編輯模式下，如果值未被修改，保持原有的資料結構
    if (this.editMode && Array.isArray(value) && this.initialValues &&
        JSON.stringify(this.initialValues[field.name]) === JSON.stringify(value)) {
      // 從 ExtraSaveDate 或原始資料中取得完整結構
      const originalData = this.ExtraSaveDate[field.name];
      if (originalData && originalData.type === 'dynamic-api-table') {
        return originalData;
      }
    }

    if (!value) {
      return {
        type: 'dynamic-api-table',
        value: []
      };
    }

    try {
      if (Array.isArray(value)) {
        // 轉換每一行資料，將欄位 ID 替換為欄位標籤
        const transformedData = value.map(row => {
          const transformedRow: any = {};

          // 遍歷每一行的欄位
          Object.keys(row).forEach(key => {
            // 從 key 中提取欄位 ID (例如從 'text_input_1.value' 提取 'text_input_1')
            const fieldId = key.replace('.value', '');

            // 嘗試從 field.selectcols 中找到對應的欄位配置
            let fieldLabel = key; // 預設使用原始 key

            if (field.selectcols && Array.isArray(field.selectcols)) {
              const columnConfig = field.selectcols.find(col => col.field === key || col.field === fieldId);
              if (columnConfig && columnConfig.alias) {
                fieldLabel = columnConfig.alias;
              } else {
                // 如果沒有找到完全符合的，移除 .value 後繼續使用原始 key
                fieldLabel = key.replace('.value', '');
              }
            } else {
              // 如果沒有 selectcols 配置，移除 .value 後繼續使用原始 key
              fieldLabel = key.replace('.value', '');
            }

            // 使用欄位標籤作為新的 key
            transformedRow[fieldLabel] = row[key];
          });

          return transformedRow;
        });

        return {
          type: 'dynamic-api-table',
          value: transformedData
        };
      }

      // 非陣列的情況，直接返回
      return {
        type: 'dynamic-api-table',
        value: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.dynamic_api_table_conversion_error') + ':', error);
      return {
        type: 'dynamic-api-table',
        value: value
      };
    }
  }

  // 轉換 auto-numbering 的值 - 簡化為只儲存編號字串
  private transformAutoNumberingValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return value;
    }

    try {
      // 簡化資料格式：只返回編號字串
      if (typeof value === 'string' || typeof value === 'number') {
        return value.toString();
      }

      if (typeof value === 'object') {
        // 從物件中提取編號值
        if (value.originalValue) {
          return value.originalValue.toString();
        }
        if (value.number) {
          return value.number.toString();
        }
        if (value.value) {
          // 處理巢狀的 value
          if (typeof value.value === 'string' || typeof value.value === 'number') {
            return value.value.toString();
          }
          if (typeof value.value === 'object' && value.value.number) {
            return value.value.number.toString();
          }
          if (typeof value.value === 'object' && value.value.originalValue) {
            return value.value.originalValue.toString();
          }
        }
      }

      return value;
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.auto_number_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 risk-assessment-matrix 的值
  private transformRiskAssessmentMatrixValue(field: FormFieldConfig, value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    try {
      const riskLevel = value.riskLevel || '未評估';
      const score = value.score || 0;

      return {
        type: 'risk-assessment-matrix',
        summary: `風險等級: ${riskLevel} (分數: ${score})`,
        riskLevel: riskLevel,
        score: score,
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.risk_assessment_matrix_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 audio-recorder 的值
  private transformAudioRecorderValue(field: FormFieldConfig, value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    try {
      const fileName = value.fileName || '';
      const duration = value.duration || 0;
      const fileSize = value.fileSize || 0;

      return {
        type: 'audio-recorder',
        summary: fileName ? `音頻: ${fileName} (${this.formatDuration(duration)})` : '無音頻',
        fileName: fileName,
        duration: duration,
        fileSize: fileSize,
        hasAudio: !!fileName,
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.audio_recording_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 video-player 的值
  private transformVideoPlayerValue(field: FormFieldConfig, value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    try {
      const fileName = value.fileName || '';
      const duration = value.duration || 0;
      const fileSize = value.fileSize || 0;

      return {
        type: 'video-player',
        summary: fileName ? `影片: ${fileName} (${this.formatDuration(duration)})` : '無影片',
        fileName: fileName,
        duration: duration,
        fileSize: fileSize,
        hasVideo: !!fileName,
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.video_playback_conversion_error') + ':', error);
      return value;
    }
  }

  // 輔助方法：格式化檔案大小
  private formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  // 輔助方法：格式化時間長度
  private formatDuration(seconds: number): string {
    if (!seconds) return '0:00';

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // 轉換 fill-in 的值
  private transformFillInFieldValue(field: FormFieldConfig, value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    try {
      // 處理實際資料結構: { checkboxStates: {}, fieldValues: {} }
      if (value.fieldValues && typeof value.fieldValues === 'object') {
        const fieldCount = Object.keys(value.fieldValues).length;
        const checkboxCount = value.checkboxStates ? Object.keys(value.checkboxStates).length : 0;

        return {
          type: 'fill-in',
          summary: `填空內容 (${fieldCount} 個欄位, ${checkboxCount} 個勾選項)`,
          fieldCount: fieldCount,
          checkboxCount: checkboxCount,
          originalValue: value
        };
      }

      // 如果有填空內容
      if (value.template && value.answers) {
        const answerCount = Object.keys(value.answers).length;
        return {
          type: 'fill-in',
          summary: `填空題 (${answerCount} 個答案)`,
          answerCount: answerCount,
          template: value.template,
          originalValue: value
        };
      }

      // 如果是字串模板
      if (typeof value === 'string') {
        const blankCount = (value.match(/_+/g) || []).length;
        return {
          type: 'fill-in',
          summary: `填空模板 (${blankCount} 個空格)`,
          blankCount: blankCount,
          originalValue: value
        };
      }

      return {
        type: 'fill-in',
        summary: '填空欄位',
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.fill_in_field_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 subform-relation 的值
  private transformFormSubformRelationValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return value;
    }

    try {
      if (Array.isArray(value)) {
        return {
          type: 'subform-relation',
          summary: `關聯表單 (${value.length} 筆記錄)`,
          recordCount: value.length,
          originalValue: value
        };
      }

      if (typeof value === 'object' && value.formId) {
        return {
          type: 'subform-relation',
          summary: `關聯表單: ${value.formName || value.formId}`,
          formId: value.formId,
          formName: value.formName,
          originalValue: value
        };
      }

      return {
        type: 'subform-relation',
        summary: '關聯表單',
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.related_form_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 tags 的值
  private transformTagsValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return [];
    }

    try {
      // 如果已經是陣列格式，直接處理陣列內容
      if (Array.isArray(value)) {
        return value.map(tag =>
          typeof tag === 'object' ? (tag.name || tag.label || tag.value || String(tag)) : String(tag)
        );
      }

      // 如果是逗號分隔的字串，轉換為陣列
      if (typeof value === 'string') {
        return value.split(',').map(t => t.trim()).filter(t => t);
      }

      // 處理舊的複雜格式（向後相容）
      if (typeof value === 'object') {
        // 如果是舊的複雜格式，提取 tags 陣列
        if (value.tags && Array.isArray(value.tags)) {
          return value.tags;
        }

        // 如果是其他物件格式，嘗試轉換
        if (value.originalValue) {
          return this.transformTagsValue(field, value.originalValue);
        }
      }

      // 默認情況，轉換為包含單一元素的陣列
      return [String(value)];
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.tag_conversion_error') + ':', error);
      return Array.isArray(value) ? value : [];
    }
  }

  // 轉換 location 的值
  private transformLocationValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return value;
    }

    try {
      if (typeof value === 'object') {
        const lat = value.lat || value.latitude;
        const lng = value.lng || value.longitude;
        const address = value.address || value.name;

        if (lat && lng) {
          return {
            type: 'location',
            summary: address ? `位置: ${address}` : `座標: ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
            latitude: lat,
            longitude: lng,
            address: address,
            originalValue: value
          };
        }

        if (address) {
          return {
            type: 'location',
            summary: `位置: ${address}`,
            address: address,
            originalValue: value
          };
        }
      }

      if (typeof value === 'string') {
        return {
          type: 'location',
          summary: `位置: ${value}`,
          address: value,
          originalValue: value
        };
      }

      return {
        type: 'location',
        summary: '位置資料',
        originalValue: value
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.location_conversion_error') + ':', error);
      return value;
    }
  }

  // 轉換 timestamp 的值
  private transformTimestampValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      const currentTimestamp = Date.now();
      return {
        value: currentTimestamp,
        summary: this.formatTimestamp(currentTimestamp)
      };
    }

    // 如果已經是轉換過的格式，直接返回內部數據
    if (typeof value === 'object' && value.type === 'timestamp') {
      return {
        value: value.value,
        summary: value.summary || this.formatTimestamp(value.value)
      };
    }

    try {
      let timestamp: number;

      if (typeof value === 'number') {
        timestamp = value;
      } else if (typeof value === 'string') {
        // 嘗試解析字串為數字
        timestamp = Number(value);
        if (isNaN(timestamp)) {
          // 如果不是數字，嘗試解析日期字串
          timestamp = new Date(value).getTime();
        }
      } else if (value instanceof Date) {
        timestamp = value.getTime();
      } else {
        // 預設使用當前時間
        timestamp = Date.now();
      }

      // 確保 timestamp 是有效的
      if (isNaN(timestamp) || timestamp <= 0) {
        timestamp = Date.now();
      }

      return {
        value: timestamp,
        summary: this.formatTimestamp(timestamp)
      };
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.timestamp_conversion_error') + ':', error);
      const fallbackTimestamp = Date.now();
      return {
        value: fallbackTimestamp,
        summary: this.formatTimestamp(fallbackTimestamp)
      };
    }
  }

  // 格式化時間戳記為可讀格式
  private formatTimestamp(timestamp: number): string {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return '無效時間';
    }
  }

  onViewAllData() {
    this.Showlist.emit(this.formName);
  }

  fillnewform(){
    this.fillform.emit(this.formName);
  }

  // 保存表單配置
  save(): void {
    // 在預覽模式下，先驗證表單名稱
    if (this.preview) {
      this.validateFormName();
      if (!this.isFormNameValid) {
        console.warn(this.i18nService.translate('dynamic_form.console.form_name_validation_failed') + ':', this.formNameError);
        alert(this.formNameError);
        return;
      }
    }

    // 檢查是否有表單欄位
    if (!this.config || this.config.length === 0) {
      console.warn(this.i18nService.translate('dynamic_form.console.cannot_save_empty_form'));
      return;
    }

    // 捕獲包含檔案資料的配置
    const configWithFiles = this.config.map(field => {
      const configCopy = { ...field };

      // 如果是 file-display 或 file-upload-manager 類型，從表單控制項中獲取檔案資料
      if ((field.type === 'file-display' || field.type === 'file-upload-manager') && this.formGroup && this.formGroup.get(field.name)) {
        const fileData = this.formGroup.get(field.name)?.value;

        if (field.type === 'file-upload-manager' && fileData && fileData.files && Array.isArray(fileData.files) && fileData.files.length > 0) {
          // file-upload-manager 使用 FileUploadValue 格式
          configCopy.files = fileData.files;
          console.log(this.i18nService.translate('dynamic_form.console.captured_file_upload_manager_data') + ':', field.name, fileData.files);
        } else if (field.type === 'file-display' && fileData && Array.isArray(fileData) && fileData.length > 0) {
          // file-display 使用檔案陣列格式
          configCopy.files = fileData;
          console.log(this.i18nService.translate('dynamic_form.console.captured_file_display_data') + ':', field.name, fileData);
        }
      }

      return configCopy;
    });

    if (this.editMode && this.editFormId) {

      console.log(this.i18nService.translate('dynamic_form.console.edit_mode'));

      // 編輯模式：使用 save_form_config API
      const dataToSave = {
        id: this.editFormId,
        formName: this.formName,
        assistantid: this.assistantid,
        parent_formid: this.parent_formid, // 新增：加入 parent_formid 參數
        config: configWithFiles,
        ...this.ExtraSaveDate // 新增：合併額外儲存的資料
      };

      console.log(this.i18nService.translate('dynamic_form.console.edit_mode_extra_save_date') + '：', this.ExtraSaveDate);
      console.log(this.i18nService.translate('dynamic_form.console.edit_mode_submit_data') + '：', dataToSave);

      const apiUrl = `${environment.apiUrl}/api/form-manager/save_form_config`;        this.http.post(apiUrl, dataToSave).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.formSaved.emit(true);
            alert(this.i18nService.translate('dynamic_form.config_update_success'));
          } else {
            console.error(this.i18nService.translate('dynamic_form.console.api_response_failed') + ':', response);
            alert(this.i18nService.translate('dynamic_form.config_update_failed'));
          }
        },
        error: (error) => {
          console.error(this.i18nService.translate('dynamic_form.console.edit_form_config_failed') + ':', error);

          let errorMessage = this.i18nService.translate('form.dynamic.update_failed');
          if (error.status === 400) {
            errorMessage = this.i18nService.translate('form.dynamic.request_error');
          } else if (error.status === 404) {
            errorMessage = this.i18nService.translate('form.dynamic.config_not_found');
          } else if (error.status === 409) {
            errorMessage = this.i18nService.translate('form.dynamic.data_conflict');
          } else if (error.status === 403) {
            errorMessage = this.i18nService.translate('form.dynamic.no_permission');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          alert(errorMessage);
        }
      });
    } else {

      console.log(this.i18nService.translate('dynamic_form.console.create_mode'));

      // 新增模式：使用原本的 API
      const dataToSave = {
        id: this.guidService.generateGuid(), // 新增：在新建資料時加入 UUID 作為 id
        companyid: this.companyid,
        formName: this.formName,
        assistantid: this.assistantid,
        parent_formid: this.parent_formid, // 新增：加入 parent_formid 參數
        config: configWithFiles,
        ...this.ExtraSaveDate // 新增：合併額外儲存的資料
      };

      console.log(this.i18nService.translate('dynamic_form.console.create_mode_extra_save_date') + '：', this.ExtraSaveDate);
      console.log(this.i18nService.translate('dynamic_form.console.create_mode_submit_data') + '：', dataToSave);

      this.databaseService.sendDataToBackend(dataToSave, "form-config")
        .then(response => {
          console.log(this.i18nService.translate('dynamic_form.console.create_form_config_success') + ':', response);
          this.formSaved.emit(true);
          this.recordAdded.emit(true);

          // 新增：發射資料建立完成事件，傳遞新建立的資料
          this.dataCreated.emit(dataToSave);
        })
        .catch(error => {
          console.error(this.i18nService.translate('dynamic_form.console.create_form_config_failed') + ':', error);
        });
    }
  }

  // 新增方法：同步檔案資料到配置（預覽模式下使用）
  private syncFileDataToConfig(formValues: any): void {
    let configChanged = false;

    // 檢查每個 file-display 和 file-upload-manager 欄位
    this.config.forEach(field => {
      if ((field.type === 'file-display' || field.type === 'file-upload-manager') && formValues[field.name]) {
        const fileData = formValues[field.name];

        // 處理不同格式的檔案資料
        let processedFileData;
        if (field.type === 'file-upload-manager' && fileData.files) {
          // file-upload-manager 返回 FileUploadValue 格式
          processedFileData = fileData.files;
        } else if (Array.isArray(fileData)) {
          // file-display 返回檔案陣列
          processedFileData = fileData;
        } else {
          return; // 不是有效的檔案資料格式
        }

        // 檢查是否為有效的檔案資料陣列
        if (Array.isArray(processedFileData) && processedFileData.length > 0) {
          // 檢查檔案資料是否有變化
          const currentFiles = field.files || [];
          const hasChanges = JSON.stringify(currentFiles) !== JSON.stringify(processedFileData);

          if (hasChanges) {
            field.files = [...processedFileData];
            configChanged = true;
            console.log(`${this.i18nService.translate('dynamic_form.console.file_data_synced_to_config')}: ${field.name}`, processedFileData);
          }
        }
      }
    });

    // 如果配置有變化，發送事件
    if (configChanged) {
      // 延遲發送事件，避免太頻繁的更新
      setTimeout(() => {
        this.configChanged.emit([...this.config]);
        console.log(this.i18nService.translate('dynamic_form.console.send_config_change_event_file_sync'), this.config);
      }, 500);
    }
  }

  /**
   * 編輯模式下的簽核流程處理（簡化版）
   * 信任 FormApprovalFlowComponent 的內建處理能力，不做複雜的手動解析
   * 只在正式存檔時執行，暫存時不執行
   */
  private async checkAndFillEmptyApprovalFlow(rawData: any): Promise<void> {
    try {
      // 第一層安全檢查：確保這是正式存檔模式
      if (!this.isFormalSave || this.isDraftMode) {
        console.log(this.i18nService.translate('dynamic_form.console.temp_mode_skip_approval_process'));
        return;
      }

      // 檢查是否有簽核流程欄位
      const approvalFlowField = this.config.find(field => field.type === 'approval-flow' || field.type === 'approval-personnel-selector');
      if (!approvalFlowField) {
        console.log(this.i18nService.translate('dynamic_form.console.no_approval_flow_field'));
        return;
      }

      console.log(this.i18nService.translate('dynamic_form.console.edit_mode_approval_auto_handled'));
      console.log(this.i18nService.translate('dynamic_form.console.approval_flow_field_config') + ':', approvalFlowField);

      // 確保有 initiatorInfo（填寫人資訊）
      if (!this.initiatorInfo) {
        await this.prepareInitiatorInfo();
        console.log(this.i18nService.translate('dynamic_form.console.initiator_info_prepared') + ':', this.initiatorInfo);
      }

      // 讓 FormApprovalFlowComponent 自己處理簽核流程解析
      // 元件會根據 approvalFlowId 和 getuserlist 自動載入和解析流程
      // 不需要在這裡做手動處理

    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.edit_mode_approval_process_preparation_error') + ':', error);
      // 不中斷表單提交流程
    }
  }


  /**
   * 準備填寫人資訊
   */
  private async prepareInitiatorInfo(): Promise<void> {
    try {
      // 從 AuthService 取得使用者資訊
      const userInfo = this.authService.getUserInfo();

      // 取得使用者部門資訊
      let departmentId = '';
      let departmentName = '';
      let departmentCode = '';

      try {
        const userDepartments = await this.getUserDepartmentFromAPI(userInfo.userid);
        if (userDepartments && userDepartments.length > 0) {
          // 取第一個部門作為主要部門
          departmentId = userDepartments[0].id;
          departmentName = userDepartments[0].name;
          departmentCode = userDepartments[0].code || '';;
          console.log(this.i18nService.translate('dynamic_form.console.successfully_get_user_department_info') + ':', userDepartments);
        } else {
          console.warn(this.i18nService.translate('dynamic_form.console.no_form_filler_department_info'));
          return;
        }
      } catch (error) {
        console.warn(this.i18nService.translate('dynamic_form.console.no_form_filler_department_info') + ':', error);
        return;
      }

      this.initiatorInfo = {
        userId: userInfo.userid,
        userName: userInfo.username,
        departmentId: departmentId,
        departmentName: departmentName,
        departmentCode: departmentCode
      };
      console.log(this.i18nService.translate('dynamic_form.console.initiator_info_prepared') + ':', this.initiatorInfo);
    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.prepare_initiator_info_error') + ':', error);
    }
  }

  /**
   * 檢查是否需要跳過前兩關簽核（簡化版）
   * @param fieldsData 已轉換的表單資料（格式：{ fieldName: { type: string, value: any } }）
   */
  private checkShouldSkipFirstTwoApprovalSteps(fieldsData: { [key: string]: { type: string; value: any } }): boolean {
    try {
      // 檢查 radiobutton_1 和 radiobutton_3 的值
      // fieldsData 格式：{ radiobutton_1: { type: 'radiobutton', value: {key: 'option1', value: '需要'} } }
      const radiobutton1Field = fieldsData['radiobutton_1'];
      const radiobutton3Field = fieldsData['radiobutton_3'];

      // ⭐ 修正：如果這兩個欄位都不存在，直接返回 false（不跳過簽核流程）
      if (!radiobutton1Field && !radiobutton3Field) {
        console.log('📋 radiobutton_1 和 radiobutton_3 欄位都不存在，不執行跳過邏輯');
        return false;
      }

      // 提取實際的 value（已經是轉換後的 {key, value} 格式）
      const radiobutton1Value = radiobutton1Field?.value;
      const radiobutton3Value = radiobutton3Field?.value;

      console.log('📋 radiobutton_1 值:', radiobutton1Value);
      console.log('📋 radiobutton_3 值:', radiobutton3Value);

      // 檢查兩個 radiobutton 的值是否都不是「需要」
      const radio1NotNeeded = this.isValueNotNeeded(radiobutton1Value);
      const radio3NotNeeded = this.isValueNotNeeded(radiobutton3Value);

      console.log('📊 radiobutton_1 不需要:', radio1NotNeeded);
      console.log('📊 radiobutton_3 不需要:', radio3NotNeeded);

      // 如果兩個都不是「需要」，則需要跳過前兩關
      return radio1NotNeeded && radio3NotNeeded;

    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.check_radiobutton_conditions_error') + ':', error);
      return false; // 出錯時保持原有流程
    }
  }

  /**
   * 檢查值是否不是「需要」
   * 支援多種資料格式：
   * - 字串: "需要" / "不需要"
   * - 嵌套格式: { value: "需要" }
   * - key/value 格式: { key: "option1", value: "需要" }
   */
  private isValueNotNeeded(value: any): boolean {
    console.log('🔍 [DEBUG] isValueNotNeeded - 檢查值:', JSON.stringify(value));

    // 1. 空值判斷
    if (!value) {
      console.log('   → 空值，判斷為「不需要」');
      return true;
    }

    // 2. 檢查 key/value 格式（radiobutton 轉換後的標準格式）
    if (value.key !== undefined && value.value !== undefined) {
      const displayValue = value.value;
      const result = displayValue !== '需要';
      console.log(`   → key/value 格式，顯示值 = "${displayValue}", 判斷為「${result ? '不需要' : '需要'}」`);
      return result;
    }

    // 3. 檢查嵌套的 value.value 格式
    if (value.value && typeof value.value === 'string') {
      const result = value.value !== '需要';
      console.log(`   → value.value 格式 = "${value.value}", 判斷為「${result ? '不需要' : '需要'}」`);
      return result;
    }

    // 4. 檢查純字串格式
    if (typeof value === 'string') {
      const result = value !== '需要';
      console.log(`   → 字串格式 = "${value}", 判斷為「${result ? '不需要' : '需要'}」`);
      return result;
    }

    // 5. 無法識別的格式，採用保守策略：預設為「需要」（不跳過簽核流程）
    console.warn('⚠️ radiobutton 值格式無法識別，保守判斷為「需要」（保留完整簽核流程）:', value);
    return false;
  }

  /**
   * 移除簽核流程前兩關（簡化版）
   */
  private removeFirstTwoApprovalSteps(fieldsData: { [key: string]: { type: string; value: any } }): void {
    try {
      // 找到簽核流程欄位
      const approvalFieldName = Object.keys(fieldsData).find(fieldName => {
        const fieldData = fieldsData[fieldName];
        return fieldData.type === 'approval-flow' || fieldData.type === 'approval-personnel-selector';
      });

      if (!approvalFieldName) {
        console.log(this.i18nService.translate('dynamic_form.console.approval_flow_field_not_found'));
        return;
      }

      console.log(this.i18nService.translate('dynamic_form.console.found_approval_flow_field') + ':', approvalFieldName);

      const approvalFieldData = fieldsData[approvalFieldName];
      if (!approvalFieldData || !approvalFieldData.value) {
        console.log(this.i18nService.translate('dynamic_form.console.approval_flow_field_data_empty'));
        return;
      }

      // 尋找 steps 並移除前兩關
      let steps = null;
      let stepsPath = '';

      // 檢查各種可能的 steps 位置
      if (approvalFieldData.value.steps && Array.isArray(approvalFieldData.value.steps)) {
        steps = approvalFieldData.value.steps;
        stepsPath = 'value.steps';
      } else if (approvalFieldData.value.value && approvalFieldData.value.value.steps && Array.isArray(approvalFieldData.value.value.steps)) {
        steps = approvalFieldData.value.value.steps;
        stepsPath = 'value.value.steps';
      } else if (approvalFieldData.value.value && approvalFieldData.value.value.value && approvalFieldData.value.value.value.steps && Array.isArray(approvalFieldData.value.value.value.steps)) {
        steps = approvalFieldData.value.value.value.steps;
        stepsPath = 'value.value.value.steps';
      }

      if (!steps || steps.length <= 2) {
        console.log(this.i18nService.translate('dynamic_form.console.steps_not_found_or_insufficient') + ':', steps ? steps.length : 0);
        return;
      }

      console.log(`${this.i18nService.translate('dynamic_form.console.original_steps_count')}: ${steps.length}, 路徑: ${stepsPath}`);
      console.log(this.i18nService.translate('dynamic_form.console.removed_steps_before') + ':', steps.slice(0, 2));

      // 移除前兩關
      steps.splice(0, 2);

      console.log(`${this.i18nService.translate('dynamic_form.console.removed_first_two_steps_success')}: ${steps.length}`);
      console.log(this.i18nService.translate('dynamic_form.console.remaining_steps') + ':', steps);

    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.remove_steps_error') + ':', error);
    }
  }

  //有設定  this.getuseridlist = true 時，才需要解析流程欄位
  private async checkAndResolveApprovalFlow(): Promise<void> {
    if (!this.config) return;

    // 查找簽核流程欄位
    const approvalFlowField = this.config.find(field => field.type === 'approval-flow' || field.type === 'approval-personnel-selector');
    if (!approvalFlowField) return;

    console.log(this.i18nService.translate('dynamic_form.console.found_approval_flow_start_parsing') + ':', approvalFlowField);

    var flowid = approvalFlowField.approvalFlowId;

    if (!flowid) {
      return;
    }

    // 準備填寫人資訊
    // 從 AuthService 取得使用者資訊
    const userInfo = this.authService.getUserInfo();

    // 取得使用者部門資訊
    let departmentId = '';
    let departmentName = '';
    let departmentCode = '';

    try {
      const userDepartments = await this.getUserDepartmentFromAPI(userInfo.userid);
      if (userDepartments && userDepartments.length > 0) {
        // 取第一個部門作為主要部門
        //TODO：這邊要思考一下，是否要支援多部門
        departmentId = userDepartments[0].id;
        departmentName = userDepartments[0].name;
        departmentCode = userDepartments[0].code || '';
        console.log(this.i18nService.translate('dynamic_form.console.user_department_success') + ':', userDepartments);
      } else {
        console.warn(this.i18nService.translate('dynamic_form.console.no_user_department_info'));
        // alert(this.i18nService.translate('dynamic_form.department_info_failed'));
        return;
      }
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.no_user_department_info') + ':', error);
      // alert(this.i18nService.translate('dynamic_form.department_info_failed'));
      return;
    }

    this.initiatorInfo = {
      userId: userInfo.userid,
      userName: userInfo.username,
      departmentId: departmentId,
      departmentName: departmentName,
      departmentCode: departmentCode
    };

    console.log(this.i18nService.translate('dynamic_form.console.initiator_info') + ':', this.initiatorInfo);

  }


  // 新增方法：透過 API 取得使用者部門資訊
  private async getUserDepartmentFromAPI(userid: string): Promise<{ id: string; name: string; code?: string }[]> {
    const apiUrl = `${environment.apiUrl}/api/departments/user-departments`;

    const params = {
      assistantNo: "ALL", //這邊使用整間公司的組織資訊
      userid: userid
    };

    console.log(this.i18nService.translate('dynamic_form.console.call_user_department_api') + ':', apiUrl, params);

    try {
      const response = await this.http.get<{
        success: boolean;
        data: { id: string; name: string; code?: string }[];
        error?: string;
        message?: string;
      }>(apiUrl, { params }).toPromise();

      if (response && response.success && response.data) {
        return response.data;
      } else {
        console.warn(this.i18nService.translate('dynamic_form.console.api_response_abnormal') + ':', response);
        return [];
      }
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.call_user_department_api_failed') + ':', error);
      throw error;
    }
  }

  // 處理多人簽名更新
  onSignaturesUpdated(fieldName: string, signatures: any[]): void {
    if (this.formGroup) {
      // 將簽名資料直接存儲為物件格式，不再轉換為 JSON 字串
      const signatureData = {
        participants: signatures,
        signedCount: signatures.filter(s => s.signature !== null).length,
        totalCount: signatures.length,
        updatedAt: new Date().toISOString()
      };

      // 更新表單控制項的值 - 直接存儲物件
      const control = this.formGroup.get(fieldName);
      if (control) {
        control.setValue(signatureData);
        control.markAsTouched();
      }

      console.log(`${this.i18nService.translate('dynamic_form.console.multi_signature_field_updated')} ${fieldName}:`, signatureData);
    }
  }

  // 取得初始參與者資料 - 用於載入編輯模式的現有資料
  getInitialParticipants(fieldName: string): any[] {
    if (this.formGroup && this.formGroup.get(fieldName)) {
      const value = this.formGroup.get(fieldName)?.value;

      if (value && typeof value === 'object') {
        // 如果是物件格式，直接返回 participants
        return value.participants || [];
      } else if (typeof value === 'string') {
        // 如果是 JSON 字串，嘗試解析
        try {
          const parsed = JSON.parse(value);
          return parsed.participants || [];
        } catch (e) {
          console.warn(`${this.i18nService.translate('dynamic_form.console.unable_parse_initial_signature')} ${fieldName}:`, e);
          return [];
        }
      }
    }

    return [];
  }
  // 轉換檔案資料格式的方法
  convertToFileItems(files: any[]): FileItem[] {
    return files.map(file => ({
      id: file.id,
      name: file.name || file.originalName,
      originalName: file.originalName || file.name,
      blobName: file.blobName, // 使用 name 作為 blobName
      size: file.size,
      type: file.type,
      uploadedAt: new Date(file.uploadedAt),
      uploading: file.uploading || false,
      uploadProgress: file.uploadProgress || 100,
      error: file.error
    }));
  }

  // 載入編輯表單資料
  private async loadFormDataForEdit(): Promise<void> {

    if (!this.editFormId || !this.companyid) {
      console.warn(this.i18nService.translate('dynamic_form.console.unable_load_edit_form_missing_data'));
      return;
    }

    try {
      console.log(this.i18nService.translate('dynamic_form.console.load_edit_form_data') + ':', { editFormId: this.editFormId, companyid: this.companyid });
      console.log(this.i18nService.translate('dynamic_form.console.call_get_form_data_api') + ':', `${environment.apiUrl}/api/form-manager/get-form-data?id=${this.editFormId}`);

      const apiUrl = `${environment.apiUrl}/api/form-manager/get-form-data`;
      const response = await this.http.get<{ success: boolean; data: any }>(apiUrl, {
        params: {
          id: this.editFormId
        }
      }).toPromise();

      console.log(this.i18nService.translate('dynamic_form.console.api_response') + ':', response);


      if (response && response.success && response.data) {

        // 特別記錄 multi-checkbox 欄位的原始資料
        const multiCheckboxFields = Object.keys(response.data).filter(key => key.includes('multi_checkbox'));

        multiCheckboxFields.forEach(fieldName => {
          console.log(`[DEBUG] Raw ${fieldName} from API:`, JSON.stringify(response.data[fieldName], null, 2));
        });

        if (response.data.multi_checkbox_1) {
        } else {
        }

         // 檢查是否有 referencedata
        if (response.data.referencedata) {
          this.referenceData = response.data.referencedata;
          console.log(this.i18nService.translate('dynamic_form.console.reference_data_loaded') + ':', this.referenceData);
        } else {
          this.referenceData = {};
        }

        // 將 API 回傳的資料轉換為 initialValues 格式
        this.initialValues = this.prepareInitialDataFromFormData(response.data);

        // 記錄轉換後的 multi-checkbox 資料
        if (this.initialValues.multi_checkbox_1) {
        }

        // 重要：確保 ExtraSaveDate 包含原始記錄的 ID 和其他欄位資料，以便編輯時使用
        if (response.data.id) {
          this.ExtraSaveDate = { ...this.ExtraSaveDate, ...response.data };
          console.log(this.i18nService.translate('dynamic_form.console.set_edit_record_to_extrasavedate') + ':', this.ExtraSaveDate);
        }

        console.log(this.i18nService.translate('dynamic_form.console.converted_initial_values') + ':', this.initialValues);
      } else {
        console.error(this.i18nService.translate('dynamic_form.console.load_edit_form_data_failed') + ':', response);
      }
    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.load_edit_form_data_error') + ':', error);
    }
  }

    // 檢查是否有引用資料
  hasReferenceData(): boolean {
    return this.referenceData &&
           typeof this.referenceData === 'object' &&
           Object.keys(this.referenceData).length > 0;
  }

  // 將 referenceData 轉換為陣列格式供 *ngFor 使用
  getReferenceDataArray(): Array<{ label: string; value: string }> {
    if (!this.hasReferenceData()) {
      return [];
    }

    return Object.keys(this.referenceData).map(key => {
      const item = this.referenceData[key];
      return {
        label: item.label || key,
        value: item.value || ''
      };
    });
  }

  // 將 API 回傳的表單資料轉換為 initialValues 格式
  private prepareInitialDataFromFormData(formData: any): any {


    const initialData: any = {};

    // 遍歷表單配置，提取對應的資料
    if (this.config && Array.isArray(this.config)) {
      this.config.forEach(field => {
        const fieldName = field.name;
        const value = formData[fieldName];

        // 特別記錄 multi-checkbox 欄位的原始資料
        if (field.type === 'multi-checkbox') {
          console.log(`[DEBUG] Raw multi-checkbox data for ${fieldName}:`, JSON.stringify(value, null, 2));
        }

        if (value !== undefined && value !== null) {
          // 特別處理 multi-checkbox - 可能沒有包裝在 type/value 結構中
          if (field.type === 'multi-checkbox' && Array.isArray(value)) {
            console.log(`[DEBUG] Direct multi-checkbox array for ${fieldName}:`, JSON.stringify(value, null, 2));
            // 直接是陣列格式，不是包裝在 {type, value} 中
            initialData[fieldName] = value.map((item: any) => {
              console.log(`[DEBUG] Processing direct multi-checkbox item:`, item);

              // 如果 option 是物件格式 {key, value}，提取 key 但保留 textValue
              if (item.option && typeof item.option === 'object' && item.option.key) {
                const result = {
                  option: item.option.key,  // 提取 key
                  selected: item.selected || false,
                  textValue: item.textValue !== undefined ? item.textValue : ''  // 確保保留 textValue
                };
                console.log(`[DEBUG] Processed item with object option:`, result);
                return result;
              }

              // 確保即使是簡單格式也保留所有欄位
              const result = {
                option: item.option,
                selected: item.selected || false,
                textValue: item.textValue !== undefined ? item.textValue : ''
              };
              console.log(`[DEBUG] Processed item with simple option:`, result);
              return result;
            });
            console.log(`[DEBUG] Processed direct multi-checkbox data final:`, JSON.stringify(initialData[fieldName], null, 2));
          } else if (typeof value === 'object' && value.value !== undefined) {
            // 處理具有類型的複雜資料結構
            switch (value.type) {
              case 'datetime':
                if (value.value) {
                  // 將 Unix 時間戳轉換為適當的格式
                  const timestamp = typeof value.value === 'number' ? value.value : Number(value.value);
                  initialData[fieldName] = !isNaN(timestamp) ? timestamp : value.value;
                }
                break;

              case 'meeting-signature-collection':
                // 處理會議簽名收集資料
                if (typeof value.value === 'string') {
                  // 如果是 JSON 字串格式，嘗試解析
                  try {
                    initialData[fieldName] = JSON.parse(value.value);
                  } catch (e) {
                    console.warn(`${this.i18nService.translate('dynamic_form.console.unable_parse_meeting_signature')} ${fieldName}:`, e);
                    initialData[fieldName] = value.value;
                  }
                } else if (value.value && value.value.participants) {
                  // 如果是新的結構化格式，直接使用
                  initialData[fieldName] = value.value;
                } else {
                  // 其他格式保持原值
                  initialData[fieldName] = value.value;
                }
                break;

              case 'file-upload-manager':
                if (value.value) {
                  // 處理檔案上傳管理器資料
                  initialData[fieldName] = value.value;
                }
                break;

              case 'dynamic-api-select':
                console.log(`[DEBUG] Processing dynamic-api-select field ${fieldName}:`, value);

                // 檢查是否已經是正確的格式
                if (value.type === 'dynamic-api-select' && value.value) {
                  // 資料已經是正確格式，直接使用
                  console.log(`[DEBUG] Data is already in correct format`);
                  initialData[fieldName] = value;
                } else if (value.value) {
                  // 舊格式，需要轉換
                  console.log(`[DEBUG] Converting old format`);
                  if (typeof value.value === 'object' && value.value.key !== undefined && value.value.value !== undefined) {
                    initialData[fieldName] = {
                      type: 'dynamic-api-select',
                      value: {
                        key: value.value.key,
                        value: value.value.value
                      }
                    };
                  } else if (typeof value.value === 'object' && value.value.id !== undefined) {
                    // 舊格式相容
                    initialData[fieldName] = value.value.id;
                  } else {
                    initialData[fieldName] = value.value;
                  }
                } else {
                  console.log(`[DEBUG] Using value as is`);
                  initialData[fieldName] = value;
                }
                console.log(`[DEBUG] Final value for ${fieldName}:`, initialData[fieldName]);
                break;

              case 'department-selector':
                if (value.value) {
                  // 處理部門選擇器資料
                  if (typeof value.value === 'object' && value.value.id !== undefined) {
                    initialData[fieldName] = value.value.id;
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'personnel-selector':
                if (value.value) {
                  // 處理人員選擇器資料
                  if (typeof value.value === 'object' && value.value.id !== undefined) {
                    initialData[fieldName] = value.value.id;
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'approval-flow':
              case 'approval-personnel-selector':
                if (value.value) {
                  // 處理簽核流程資料
                  initialData[fieldName] = value.value;
                }
                break;

              case 'signature':
                if (value.value) {
                  // 處理簽名資料 - 檢查多種可能的資料結構

                  if (value.value.originalValue) {
                    // 如果有 originalValue，使用它（編輯模式常見的結構）
                    initialData[fieldName] = value.value.originalValue;
                  } else if (value.value.imageData) {
                    // 如果直接有 imageData，使用 value.value
                    initialData[fieldName] = value.value;
                  } else {
                    // 其他情況，保持原值
                    initialData[fieldName] = value.value;
                  }

                }
                break;

              case 'fill-in':
                if (value.value) {
                  // 處理填空欄位資料
                  if (value.value.originalValue && value.value.originalValue.checkboxStates && value.value.originalValue.fieldValues) {
                    // 如果有 originalValue，使用其內部的 checkboxStates 和 fieldValues
                    initialData[fieldName] = value.value.originalValue;
                  } else if (value.value.checkboxStates && value.value.fieldValues) {
                    // 如果直接就是所需格式，直接使用
                    initialData[fieldName] = value.value;
                  } else {
                    // 其他格式保持原值
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'tags':
                if (value.value) {
                  // 處理標籤資料
                  if (value.value.originalValue && Array.isArray(value.value.originalValue)) {
                    initialData[fieldName] = value.value.originalValue;
                  } else if (Array.isArray(value.value)) {
                    initialData[fieldName] = value.value;
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'location':

                if (value.value) {
                  // 優先使用 originalValue（原始位置字串）
                  if (value.value.originalValue) {
                    initialData[fieldName] = value.value.originalValue;
                  }
                  // 如果有 address，使用它
                  else if (value.value.address) {
                    initialData[fieldName] = value.value.address;
                  }
                  // 如果有座標，組合成字串
                  else if (value.value.latitude && value.value.longitude) {
                    initialData[fieldName] = `${value.value.latitude}, ${value.value.longitude}`;
                  }
                  // 否則保持原值
                  else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'timestamp':
                if (value.value) {
                  // 處理時間戳記資料，支援雙重巢狀結構
                  let timestamp: number;

                  if (typeof value.value === 'object' && value.value.value) {
                    // 處理雙重巢狀：value.value.value
                    timestamp = typeof value.value.value === 'number' ? value.value.value : Number(value.value.value);
                  } else {
                    // 處理單層：value.value
                    timestamp = typeof value.value === 'number' ? value.value : Number(value.value);
                  }

                  initialData[fieldName] = !isNaN(timestamp) ? timestamp : Date.now();
                } else {
                  // 如果沒有值，使用當前時間
                  initialData[fieldName] = Date.now();
                }
                break;

              case 'photo-upload':
                if (value.value) {
                  // 處理照片上傳資料
                  initialData[fieldName] = Array.isArray(value.value) ? value.value : value.value;
                }
                break;

              case 'avatar-upload':
                if (value.value) {
                  // 處理大頭照上傳資料
                  initialData[fieldName] = value.value;
                }
                break;

              case 'audio-recorder':
                if (value.value) {
                  // 處理錄音資料
                  initialData[fieldName] = value.value;
                }
                break;

              case 'auto-numbering':
                if (value.value) {
                  // 處理自動編號資料 - 支援新的簡化格式和舊格式
                  if (typeof value.value === 'string' || typeof value.value === 'number') {
                    // 新格式：直接儲存字串
                    initialData[fieldName] = value.value.toString();
                  } else if (typeof value.value === 'object') {
                    // 舊格式相容性：從物件中提取編號
                    if (value.value.originalValue) {
                      initialData[fieldName] = value.value.originalValue;
                    } else if (value.value.number) {
                      initialData[fieldName] = value.value.number;
                    } else {
                      initialData[fieldName] = value.value;
                    }
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'select':
                if (value.value) {
                  // 處理下拉選單資料
                  if (typeof value.value === 'object' && value.value.key !== undefined) {
                    initialData[fieldName] = value.value.key;
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'radiobutton':
                if (value.value) {
                  // 處理單選按鈕資料 - 保持完整的資料結構以供元件使用
                  initialData[fieldName] = value.value;
                }
                break;

              case 'multi-checkbox':
                if (value.value) {
                  // 處理多選核取方塊資料
                  // 檢查是否有巢狀結構需要解開
                  if (value.value.originalValue) {
                    initialData[fieldName] = value.value.originalValue;
                  } else if (Array.isArray(value.value)) {
                    // 保留完整的資料結構，包括 textValue
                    initialData[fieldName] = value.value.map((item: any) => {

                      // 如果 option 是物件格式 {key, value}
                      if (item.option && typeof item.option === 'object' && item.option.key) {
                        const result = {
                          option: item.option.key,  // 提取 key 作為 option 值
                          selected: item.selected || false,
                          textValue: item.textValue !== undefined ? item.textValue : ''  // 確保保留 textValue，即使是空字串
                        };
                        return result;
                      }
                      // 如果是簡單格式，確保保留所有欄位
                      const result = {
                        option: item.option,
                        selected: item.selected || false,
                        textValue: item.textValue !== undefined ? item.textValue : ''
                      };
                      return result;
                    });
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'required-document-upload':
                if (value.value) {
                  // 處理指定文件上傳資料
                  console.log(`[DynamicForm] Processing required-document-upload field [${fieldName}]:`, value.value);

                  if (value.value.originalValue) {
                    // 如果有 originalValue，使用其結構
                    console.log(`[DynamicForm] Using originalValue for [${fieldName}]:`, value.value.originalValue);
                    initialData[fieldName] = value.value.originalValue;
                  } else if (value.value.file && value.value.aiParseResult) {
                    // 如果是新格式的結構，直接使用
                    console.log(`[DynamicForm] Using direct format for [${fieldName}]:`, value.value);
                    initialData[fieldName] = value.value;
                  } else {
                    // 其他格式保持原值
                    console.log(`[DynamicForm] Using raw value for [${fieldName}]:`, value.value);
                    initialData[fieldName] = value.value;
                  }

                  console.log(`[DynamicForm] Final initialData for [${fieldName}]:`, initialData[fieldName]);
                }
                break;

              case 'date-range':
                if (value.value) {
                  // 處理日期範圍資料
                  if (typeof value.value === 'object' && value.value.startDate && value.value.endDate) {
                    initialData[fieldName] = {
                      startDate: value.value.startDate,
                      endDate: value.value.endDate
                    };
                  } else {
                    initialData[fieldName] = value.value;
                  }
                }
                break;

              case 'dynamic-api-table':
                if (value.value) {
                  // 處理動態 API 表格資料 - 新的簡化格式
                  console.log(`[DynamicForm] Processing dynamic-api-table field [${fieldName}]:`, value.value);

                  if (Array.isArray(value.value)) {
                    // 新格式：value 直接是陣列
                    console.log(`[DynamicForm] Using array format for [${fieldName}]:`, value.value);

                    // 需要將顯示標籤轉換回欄位 ID 格式，以便元件使用
                    const field = this.config.find(f => f.name === fieldName);
                    if (field && field.selectcols && Array.isArray(field.selectcols)) {
                      // 建立 alias 到 field 的對映
                      const aliasToField: { [key: string]: string } = {};
                      field.selectcols.forEach(col => {
                        if (col.alias && col.field) {
                          aliasToField[col.alias] = col.field;
                        }
                      });

                      // 轉換每一行資料
                      const convertedData = value.value.map((row: any) => {
                        const convertedRow: any = {};
                        Object.keys(row).forEach(key => {
                          // 如果 key 是 alias，轉換回 field
                          const fieldKey = aliasToField[key] || key;
                          // 確保欄位 key 包含 .value 後綴
                          const finalKey = fieldKey.includes('.value') ? fieldKey : `${fieldKey}.value`;
                          convertedRow[finalKey] = row[key];
                        });
                        return convertedRow;
                      });

                      initialData[fieldName] = convertedData;
                    } else {
                      // 沒有 selectcols 配置，直接使用原始資料
                      initialData[fieldName] = value.value;
                    }
                  } else {
                    // 其他格式保持原值
                    console.log(`[DynamicForm] Using raw value for [${fieldName}]:`, value.value);
                    initialData[fieldName] = value.value;
                  }

                  console.log(`[DynamicForm] Final initialData for [${fieldName}]:`, initialData[fieldName]);
                }
                break;

              default:
                // 其他類型直接使用 value 屬性
                initialData[fieldName] = value.value;
                break;
            }
          } else {
            // 簡單值直接使用（如 entrydate 等）
            initialData[fieldName] = value;
          }
        }
      });
    }

    return initialData;
  }

  // ===== 階段化表單方法 =====

  loadStageConfiguration(): void {
    // 從表單配置載入階段資訊
    // 這裡先使用模擬的邏輯，實際應該從後端載入
    const savedStageConfig = localStorage.getItem(`stageConfig_${this.formName}`);
    if (savedStageConfig) {
      try {
        const stageData = JSON.parse(savedStageConfig);
        this.enableStageMode = stageData.enableStageMode || false;
        this.formStages = stageData.formStages || [];
        if (this.enableStageMode && this.formStages.length > 0) {
          this.initializeCurrentStage();
        }
      } catch (error) {
        console.error(this.i18nService.translate('dynamic_form.console.load_stage_config_failed') + ':', error);
      }
    }
  }

  /**
   * 方案 B：針對施工申請單設定日期限制
   * 根據特殊作業類型動態設定作業起訖日的最小開始日期
   */
  private applyWorkApplicationDateConstraints(): void {
    // 只針對施工申請單表單（JSAI）
    const WORK_APPLICATION_FORM_ID = '6a8bed49-6f22-4977-9417-b2e2a43f1f6d';

    console.log('[WorkApplication] 🔍 檢查是否為施工申請單, formid:', this.formid);

    if (this.formid !== WORK_APPLICATION_FORM_ID) {
      console.log('[WorkApplication] ❌ 不是施工申請單，跳過日期限制設定');
      return;
    }

    console.log('[WorkApplication] ✅ 開始套用施工申請單日期限制');
    console.log('[WorkApplication] 📋 當前 config:', this.config);

    // 找到 date_range_1 欄位（作業起訖日）
    const dateRangeField = this.config?.find((field: FormFieldConfig) => field.id === 'date_range_1');

    if (!dateRangeField) {
      console.warn('[WorkApplication] ⚠️ 找不到 date_range_1 欄位！');
      console.log('[WorkApplication] 📋 所有欄位 IDs:', this.config?.map(f => f.id));
      return;
    }

    console.log('[WorkApplication] ✅ 找到 date_range_1 欄位:', dateRangeField);

    // 檢查是否包含「吊掛」作業
    const hasHangingWork = this.checkIfHasHangingWork();

    // 計算最小開始日期
    const today = new Date();
    const daysToAdd = hasHangingWork ? 21 : 7;
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + daysToAdd);

    // 格式化為 YYYY-MM-DD
    const minStartDate = minDate.toISOString().split('T')[0];

    console.log('[WorkApplication] 📅 計算結果:', {
      today: today.toISOString().split('T')[0],
      hasHangingWork,
      daysToAdd,
      minStartDate
    });

    // 設定欄位的最小日期（簡化版：只用 minDate）
    dateRangeField.minDate = minStartDate;
    console.log('[WorkApplication] ⚙️ 已設定 dateRangeField.minDate =', minStartDate);

    // 設定提示訊息（告訴使用者為什麼有日期限制）
    if (hasHangingWork) {
      dateRangeField.hint = `施工日期最少需在申請日 21 天後（${minStartDate}），因包含吊掛作業`;
    } else {
      dateRangeField.hint = `施工日期最少需在申請日 7 天後（${minStartDate}）`;
    }
    console.log('[WorkApplication] ⚙️ 已設定 hint 提示訊息:', dateRangeField.hint);

    console.log('[WorkApplication] ✅ 日期限制套用完成！最終 dateRangeField:', dateRangeField);
  }

  /**
   * 檢查是否包含「吊掛」作業
   * 優先檢查 initialValues，其次檢查 referenceData
   */
  private checkIfHasHangingWork(): boolean {
    // 方法1: 檢查 initialValues 中的 referencedata_special-operations
    if (this.initialValues && this.initialValues['referencedata_special-operations']) {
      const flattenedData = this.initialValues['referencedata_special-operations'];

      if (flattenedData.value && Array.isArray(flattenedData.value)) {
        const hasHanging = flattenedData.value.includes('吊掛');
        console.log('[WorkApplication] 從 initialValues 檢查吊掛作業:', hasHanging, flattenedData.value);
        return hasHanging;
      }
    }

    // 方法2: 檢查 initialValues 中的 referencedata.special-operations
    if (this.initialValues && this.initialValues.referencedata && this.initialValues.referencedata['special-operations']) {
      const specialOpsData = this.initialValues.referencedata['special-operations'];

      if (specialOpsData.value && Array.isArray(specialOpsData.value)) {
        const hasHanging = specialOpsData.value.includes('吊掛');
        console.log('[WorkApplication] 從 initialValues.referencedata 檢查吊掛作業:', hasHanging, specialOpsData.value);
        return hasHanging;
      }
    }

    // 方法3: 檢查 referenceData
    if (this.referenceData && this.referenceData['special-operations']) {
      const specialOpsData = this.referenceData['special-operations'];

      if (specialOpsData.value && Array.isArray(specialOpsData.value)) {
        const hasHanging = specialOpsData.value.includes('吊掛');
        console.log('[WorkApplication] 從 referenceData 檢查吊掛作業:', hasHanging, specialOpsData.value);
        return hasHanging;
      }
    }

    console.log('[WorkApplication] 找不到特殊作業資料，預設為非吊掛作業');
    return false;
  }

  initializeCurrentStage(): void {
    // 根據用戶權限和表單狀態決定當前階段
    this.currentStageIndex = this.determineUserStage();
    this.currentStage = this.formStages[this.currentStageIndex] || null;
  }

  determineUserStage(): number {
    // 簡化邏輯：總是從第一個階段開始
    // 實際實作應該根據用戶權限和表單狀態來決定
    return 0;
  }

  getFilteredSectionsByStage(): any[] {
    if (!this.enableStageMode || !this.currentStage) {
      return this.groupedSections;
    }

    // 過濾出當前階段和已完成階段的欄位
    return this.groupedSections.map(section => ({
      ...section,
      fields: section.fields.filter(field => this.isFieldVisibleInCurrentStage(field))
    })).filter(section => section.fields.length > 0);
  }

  isFieldVisibleInCurrentStage(field: FormFieldConfig): boolean {
    if (!this.enableStageMode) return true;

    // 沒有階段歸屬的欄位總是可見
    if (!field.stageId) return true;

    // 當前階段的欄位可編輯
    if (field.stageId === this.currentStage?.id) return true;

    // 已完成階段的欄位只讀可見
    const fieldStageIndex = this.formStages.findIndex(s => s.id === field.stageId);
    return fieldStageIndex < this.currentStageIndex;
  }

  completeCurrentStage(): void {
    if (!this.validateCurrentStage()) {
      return;
    }

    // 標記當前階段為完成
    if (this.currentStage) {
      this.currentStage.status = 'completed';
      this.currentStage.completedBy = this.Username;
      this.currentStage.completedAt = new Date();
    }

    // 進入下一階段
    if (this.currentStageIndex < this.formStages.length - 1) {
      this.currentStageIndex++;
      this.currentStage = this.formStages[this.currentStageIndex];
      this.currentStage.status = 'in_progress';
    }

    // 保存階段狀態
    this.saveStageProgress();
  }

  validateCurrentStage(): boolean {
    // 驗證當前階段的必填欄位
    const currentStageFields = this.getCurrentStageFields();
    return currentStageFields.every(field => this.isFieldValid(field));
  }

  getCurrentStageFields(): FormFieldConfig[] {
    if (!this.currentStage) return [];
    return this.config.filter(field => field.stageId === this.currentStage!.id);
  }

  isFieldValid(field: FormFieldConfig): boolean {
    const control = this.formGroup.get(field.name);
    return !field.required || (control && control.value && control.valid);
  }

  canCompleteCurrentStage(): boolean {
    return this.currentStage?.status === 'in_progress' &&
           this.hasPermissionForCurrentStage();
  }

  hasPermissionForCurrentStage(): boolean {
    // 簡化邏輯：總是返回 true
    // 實際實作應該檢查用戶是否有權限完成當前階段
    return true;
  }

  canGoToPreviousStage(): boolean {
    return this.currentStageIndex > 0 &&
           this.hasPermissionToModifyPreviousStage();
  }

  hasPermissionToModifyPreviousStage(): boolean {
    // 簡化邏輯：總是返回 false
    // 實際實作應該檢查用戶是否有權限修改前一階段
    return false;
  }

  goToPreviousStage(): void {
    if (this.canGoToPreviousStage()) {
      this.currentStageIndex--;
      this.currentStage = this.formStages[this.currentStageIndex];
    }
  }

  isLastStage(): boolean {
    return this.currentStageIndex === this.formStages.length - 1;
  }

  saveStageProgress(): void {
    // 保存階段進度到後端或本地儲存
    const stageData = {
      enableStageMode: this.enableStageMode,
      formStages: this.formStages,
      currentStageIndex: this.currentStageIndex
    };
    localStorage.setItem(`stageProgress_${this.formName}`, JSON.stringify(stageData));
  }

  // 取消表單
  cancel(): void {
    this.formCancelled.emit();
  }

  // 風險評估矩陣相關方法
  getFieldValue(fieldName: string): any {
    return this.formGroup?.get(fieldName)?.value || null;
  }

  onRiskAssessmentChange(fieldName: string, value: any): void {
    if (this.formGroup && this.formGroup.get(fieldName)) {
      this.formGroup.get(fieldName)?.setValue(value);
    }

}

// 表單名稱驗證方法
  validateFormName(): void {
    this.formNameError = '';
    this.isFormNameValid = false;

    // 檢查是否為空值
    if (!this.formName || this.formName.trim() === '') {
      this.formNameError = this.i18nService.translate('form.validation.form_name_required');
      this.isFormNameValid = false;
      return;
    }

    // 檢查字數限制
    if (this.formName.length > 50) {
      this.formNameError = this.i18nService.translate('form.validation.form_name_max_length', { max: 50 });
      this.isFormNameValid = false;
      return;
    }

    // 檢查是否包含特殊字元
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(this.formName)) {
      this.formNameError = this.i18nService.translate('form.validation.form_name_invalid_chars');
      this.isFormNameValid = false;
      return;
    }

    // 通過所有驗證
    this.isFormNameValid = true;
  }

  /**
   * 檢查並發送第一關簽核通知
   * 只在正式存檔時執行，暫存時不執行
   */
  private async checkAndSendFirstStageNotification(formData: any): Promise<void> {
    try {
      // 首要檢查：確保這是正式存檔而非暫存
      if (!this.isFormalSave) {
        console.log(this.i18nService.translate('dynamic_form.console.draft_mode_skip_approval_notification'));
        return;
      }

      // 🔍 檢查是否有簽核流程（支援兩種類型）
      // 1. approval_flow_1 - FormApprovalFlowComponent (簽核流程配置)
      // 2. approval_personnel-selector_1 - FormApprovalPersonnelSelectorComponent (自行指定簽核人員)
      let approvalFlowField = formData.approval_flow_1 || formData['approval_personnel-selector_1'];

      console.log('🔍 檢查表單資料中的簽核流程:', {
        approval_flow_1: formData.approval_flow_1 ? '存在' : '不存在',
        'approval_personnel-selector_1': formData['approval_personnel-selector_1'] ? '存在' : '不存在',
        selectedField: approvalFlowField ? '找到' : '未找到'
      });

      if (!approvalFlowField || !approvalFlowField.value) {
        console.log(this.i18nService.translate('dynamic_form.console.no_approval_flow_notification'));
        return;
      }

      console.log(this.i18nService.translate('dynamic_form.console.formal_save_mode_detected_approval_flow') + ':', approvalFlowField);

      // 提取第一關簽核人員
      const firstStepApprovers = this.getFirstStepApprovers(approvalFlowField.value);
      if (!firstStepApprovers || firstStepApprovers.length === 0) {
        console.log(this.i18nService.translate('dynamic_form.console.first_step_approvers_empty_skip_notification'));
        return;
      }

      console.log(this.i18nService.translate('dynamic_form.console.first_step_approvers') + ':', firstStepApprovers);

      // 取得電子郵件並發送通知
      await this.sendNotificationToFirstStageApprovers(firstStepApprovers, formData);

    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.send_first_step_approval_notification_failed') + ':', error);
      // 不要讓郵件發送失敗影響表單提交流程
    }
  }

  /**
   * 從簽核流程資料中提取第一關簽核人員
   */
  private getFirstStepApprovers(approvalFlowData: any): string[] {
    try {
      console.log('🔍 [getFirstStepApprovers] 原始輸入資料:', JSON.stringify(approvalFlowData, null, 2));

      // 處理雙層嵌套的 value 結構
      let actualFlowData = approvalFlowData;

      // 如果有雙層 value 嵌套，取得內層的 value
      if (actualFlowData && actualFlowData.value && actualFlowData.value.value) {
        actualFlowData = actualFlowData.value.value;
        console.log('🔍 [getFirstStepApprovers] 檢測到雙層 value 嵌套，解包後:', JSON.stringify(actualFlowData, null, 2));
      } else if (actualFlowData && actualFlowData.value) {
        actualFlowData = actualFlowData.value;
        console.log('🔍 [getFirstStepApprovers] 檢測到單層 value 嵌套，解包後:', JSON.stringify(actualFlowData, null, 2));
      }

      // 檢查資料結構
      if (!actualFlowData || !actualFlowData.steps || !Array.isArray(actualFlowData.steps)) {
        console.warn('⚠️ [getFirstStepApprovers] 簽核流程資料結構不正確:', actualFlowData);
        return [];
      }

      console.log(`🔍 [getFirstStepApprovers] 找到 ${actualFlowData.steps.length} 個步驟`);
      console.log('🔍 [getFirstStepApprovers] 所有步驟:', actualFlowData.steps.map((s: any) => ({
        name: s.name || s.stepName,
        order: s.order,
        level: s.level,
        approvers: s.approvers,
        approverList: s.approverList
      })));

      // 找到最前面的關卡（最小的 order 或 level）
      // 注意：因為有些關卡是條件性的（如用電審核），所以不一定從 1 開始
      const stepsWithOrder = actualFlowData.steps.map((step: any) => ({
        ...step,
        sortKey: step.order || step.level || 999
      }));

      stepsWithOrder.sort((a: any, b: any) => a.sortKey - b.sortKey);
      const firstStep = stepsWithOrder[0];

      if (!firstStep) {
        console.warn('⚠️ [getFirstStepApprovers] 找不到任何步驟');
        console.log('🔍 [getFirstStepApprovers] 可用的步驟:', actualFlowData.steps);
        return [];
      }

      console.log('✅ [getFirstStepApprovers] 找到第一關步驟 (order/level: ' + firstStep.sortKey + '):', firstStep);

      // 檢查簽核人員列表
      // approverList 是實際的簽核人員列表（包含 id 和 name）
      // approvers 是簽核人員的定義配置（包含 type、departmentId、roleId 等）
      const approverList = firstStep.approverList;
      if (!approverList || !Array.isArray(approverList)) {
        console.warn('⚠️ [getFirstStepApprovers] 第一關沒有 approverList 或不是陣列:', firstStep);
        console.log('🔍 [getFirstStepApprovers] firstStep.approverList:', approverList);
        console.log('🔍 [getFirstStepApprovers] firstStep.approvers (定義配置):', firstStep.approvers);
        return [];
      }

      console.log('🔍 [getFirstStepApprovers] approverList 內容:', approverList);

      // 提取簽核人員ID
      const approverIds = approverList
        .map((approver: any) => approver.id || approver.userId)
        .filter((id: any) => id);

      console.log('✅ [getFirstStepApprovers] 提取到的第一關簽核人員ID:', approverIds);
      return approverIds;

    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.extract_first_step_approvers_failed') + ':', error);
      return [];
    }
  }

  /**
   * 發送通知給第一關簽核人員
   */
  private async sendNotificationToFirstStageApprovers(approverIds: string[], formData: any): Promise<void> {
    try {
      // 取得當前語系
      const currentLocale = this.getCurrentLanguageSync();

      // 取得電子郵件地址とユーザー情報
      const emailResponse = await this.getUserEmails(approverIds);
      console.log(this.i18nService.translate('dynamic_form.console.email_retrieval_result') + ':', emailResponse);

      // 📧 usersInfoから全ての情報を抽出（データ整合性を保証）
      if (emailResponse && emailResponse.success && emailResponse.usersInfo && Array.isArray(emailResponse.usersInfo) && emailResponse.usersInfo.length > 0) {
        
        // 📧 usersInfoからメールアドレスと氏名を同時に抽出
        const emailToNameMap = new Map<string, string>();
        const recipientEmails: string[] = [];

        emailResponse.usersInfo.forEach((user: any) => {
          if (user && user.email) {
            // メールアドレスを配列に追加
            recipientEmails.push(user.email);
            
            // 氏名マッピングを作成（優先順位: fullName > username > email local-part）
            const displayName = String(user.fullName || user.username || user.email.split('@')[0] || '').trim();
            emailToNameMap.set(user.email, displayName);
          }
        });

        console.log('📧 抽出されたメールアドレス:', recipientEmails);
        console.log('📧 氏名マッピング:', Array.from(emailToNameMap.entries()));

        if (recipientEmails.length === 0) {
          console.warn(this.i18nService.translate('dynamic_form.console.unable_get_first_step_approver_emails'), 'usersInfoからメールアドレスを抽出できませんでした');
          return;
        }

        // 取得實際的流程資料（處理雙層嵌套）
        let actualFlowData = formData.approval_flow_1?.value;
        if (actualFlowData && actualFlowData.value && actualFlowData.value.value) {
          actualFlowData = actualFlowData.value.value;
        } else if (actualFlowData && actualFlowData.value) {
          actualFlowData = actualFlowData.value;
        }

        // 找出第一關步驟的索引（用於 URL）
        // 這樣點擊連結後可以定位到第一關簽核步驟
        let firstStepIndex = 0;
        if (actualFlowData?.steps && Array.isArray(actualFlowData.steps)) {
          const stepsWithOrder = actualFlowData.steps.map((step: any, idx: number) => ({
            originalIndex: idx,
            sortKey: step.order || step.level || 999
          }));
          stepsWithOrder.sort((a: any, b: any) => a.sortKey - b.sortKey);
          firstStepIndex = stepsWithOrder[0]?.originalIndex || 0;
          console.log('🔍 [第一關通知] 第一關步驟在陣列中的索引:', firstStepIndex);
        }

        // 提取專案名稱或工程名稱
        let projectName = this.i18nService.translate('dynamic_form.default.unnamed_project');

        console.log('🔍 [第一關通知] ===== 開始提取專案名稱 =====');

        // ⚠️ 重要：formData 是直接從表單提交的資料，不包含 referencedata
        // 需要從 API 取得完整的表單資料（包含 referencedata）
        try {
          if (formData.id) {
            console.log('🔍 [第一關通知] 從 API 取得完整表單資料，id:', formData.id);

            const apiUrl = environment.apiUrl;
            const formDataResponse = await this.http.get<any>(
              `${apiUrl}/api/form-manager/get-form-data`,
              {
                params: {
                  companyid: environment.companyid,
                  id: formData.id
                }
              }
            ).toPromise();

            console.log('🔍 [第一關通知] API 回應 success:', formDataResponse?.success);

            if (formDataResponse && formDataResponse.success && formDataResponse.data) {
              const apiFormData = formDataResponse.data;

              console.log('🔍 [第一關通知] 檢查 referencedata:', apiFormData.referencedata);

              // 從 referencedata 中取得 work-name
              if (apiFormData.referencedata?.['work-name']?.value) {
                projectName = apiFormData.referencedata['work-name'].value;
                console.log('✅ [第一關通知] 成功從 API referencedata["work-name"] 取得專案名稱:', projectName);
              } else {
                console.warn('⚠️ [第一關通知] referencedata["work-name"] 不存在或無值');
              }
            } else {
              console.warn('⚠️ [第一關通知] API 回應格式不正確或無資料');
            }
          } else {
            console.warn('⚠️ [第一關通知] formData.id 不存在，無法從 API 取得資料');
          }
        } catch (error) {
          console.error('❌ [第一關通知] 從 API 取得表單資料失敗:', error);
        }

        console.log('🔍 [第一關通知] 最終使用的專案名稱:', projectName);
        console.log('🔍 [第一關通知] ===== 專案名稱提取完成 =====');

        // 🔍 DEBUG: ステップ情報の確認
        console.log('🔍 [DEBUG] actualFlowData?.steps:', actualFlowData?.steps);
        if (actualFlowData?.steps && actualFlowData.steps.length > 0) {
          const firstStep = actualFlowData.steps[firstStepIndex] || actualFlowData.steps[0];
        }
        // 🔧 get formname
        let actualFormName = this.formName || '新表單';
        const formIdToUse = this.formid || formData.componentname;
        
        if (formIdToUse) {
          try {
            const apiUrl = environment.apiUrl;
            const formConfigResponse = await this.http.get<any>(
              `${apiUrl}/api/form-manager/get_specific_form_config`,
              {
                params: {
                  formid: formIdToUse
                }
              }
            ).toPromise();
            
            if (formConfigResponse && formConfigResponse.success && formConfigResponse.data) {
              // get formname
              const formDisplayName = formConfigResponse.data.name || 
                                     formConfigResponse.data.formName ||
                                     formConfigResponse.data.displayName;
              
              if (formDisplayName) {
                actualFormName = formDisplayName;
              }
            }
          } catch (error) {
            console.error('❌ fail to get form definitions:', error);
          }
        }
        
        // 🔧 第一關ステップ名を取得（複数の候補を試行）
        let firstStepName: string | undefined = undefined;
        let firstStepLevel: number | undefined = undefined;
        
        if (actualFlowData?.steps && actualFlowData.steps.length > 0) {
          const firstStep = actualFlowData.steps[firstStepIndex] || actualFlowData.steps[0];
          firstStepName = firstStep?.stepName || 
                         firstStep?.name || 
                         `第1關` ||
                         undefined;
          firstStepLevel = firstStep?.level || 
                          firstStep?.order || 
                          1 ||
                          undefined;
        } else {
          console.warn('⚠️ [DEBUG] actualFlowData?.steps is undefined:', {
            hasActualFlowData: !!actualFlowData,
            hasSteps: !!actualFlowData?.steps,
            stepsLength: actualFlowData?.steps?.length || 0
          });
        }

        // 準備簽核資訊
        const approvalInfo = {
          formName: actualFormName,
          projectName: projectName, // 使用專案名稱
          flowName: actualFlowData?.flowName || '簽核流程',
          currentStepName: '', // 第一關前沒有前一關
          currentStepLevel: 0,
          nextStepName: firstStepName,
          nextStepLevel: firstStepLevel,
          previousApproverName: this.getCurrentUserName() || '表單建立者',
          previousApprovalResult: '創建', // 第一關特殊標記
          taskUrl: this.generateTaskUrl(formData.id, firstStepIndex), // 傳遞 recordId 和步驟索引
          deadline: undefined,
          companyId: environment.companyid,
          submittedAt: formData.submittedAt  // 📅 申請日時を追加
        };

        // 發送通知郵件
        const emailResult = await this.sendApprovalNotification(
          recipientEmails,  // 📧 usersInfoから抽出したメールアドレス配列
          approvalInfo, 
          currentLocale,
          emailToNameMap  // 📧 氏名マッピングを渡す
        );
        console.log(this.i18nService.translate('dynamic_form.console.first_step_approval_email_sent_success'), {
          recipientCount: recipientEmails.length,
          locale: currentLocale,
          result: emailResult
        });
      } else {
        console.warn(this.i18nService.translate('dynamic_form.console.unable_get_first_step_approver_emails'), emailResponse);
      }
    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.send_first_step_approval_notification_failed') + ':', error);
      throw error;
    }
  }

  /**
   * 取得使用者電子郵件地址
   */
  private getUserEmails(userIds: string[]): Promise<any> {
    const companyId = environment.companyid;
    const apiUrl = environment.apiUrl;

    if (!companyId || !apiUrl) {
      console.error('Missing companyId or apiUrl in environment');
      return Promise.reject(new Error('Environment configuration missing'));
    }

    const url = `${apiUrl}/api/users/get-emails-by-ids`;
    const requestBody = {
      companyid: companyId,  // 使用正確的參數名稱
      ids: userIds           // 使用正確的參數名稱
    };

    console.log(this.i18nService.translate('dynamic_form.console.send_email_address_query_request') + ':', requestBody);

    return this.http.post(url, requestBody).toPromise()
      .catch(error => {
        console.error(this.i18nService.translate('dynamic_form.console.get_email_addresses_failed') + ':', error);
        throw error;
      });
  }

  /**
   * 發送簽核通知郵件
   */
  private async sendApprovalNotification(
    recipientEmails: string[],
    approvalInfo: any,
    locale: 'zh-TW' | 'ja-JP' | 'en-US',
    recipientNames?: Map<string, string>  // 📧 氏名マッピングを追加
  ): Promise<void> {
    try {
      const result = await this.emailService.sendApprovalNotification(
        recipientEmails,
        approvalInfo,
        locale,
        recipientNames  // 📧 氏名マッピングを渡す
      ).toPromise();

      console.log(this.i18nService.translate('dynamic_form.console.approval_notification_email_result') + ':', result);
      return result;
    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.send_approval_notification_email_failed') + ':', error);
      throw error;
    }
  }

  /**
   * 取得當前語言值（同步），並確保是支援的語系
   */
  private getCurrentLanguageSync(): 'zh-TW' | 'en-US' | 'ja-JP' {
    const currentLang = this.i18nService.currentLangValue;

    // 確保回傳的語系是郵件服務支援的語系
    switch (currentLang) {
      case 'ja-JP':
        return 'ja-JP';
      case 'en-US':
        return 'en-US';
      case 'zh-TW':
      default:
        return 'zh-TW';
    }
  }

  /**
   * 取得當前使用者名稱
   */
  private getCurrentUserName(): string {
    try {
      const userInfo = this.authService.getUserInfo();
      return userInfo?.username || '使用者';
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.unable_get_current_username') + ':', error);
      return '使用者';
    }
  }

  /**
   * 生成任務連結
   * 生成承認一覧画面へのリンク
   */
  private generateTaskUrl(recordId?: string, stepIndex: number = 0): string {
    try {
      const baseUrl = window.location.origin;
      
      // 🔧 根據 group_id 設定決定使用哪個元件
      const groupId = environment.group_id || 'JSAI';
      const componentName = (groupId === 'IAD')
        ? 'FormApprovalListComponent'    // IAD → 承認一覧
        : 'workApplicationComponent';    // JSAI → 施工申請
      
      const assistantId = this.assistantUrlService.getCurrentAssistantId();

      // JSAI-799: 如果有 recordId，生成指向簽核視窗的 URL
      // 格式: /assistant?id={assistantId}&component={componentName}&formId={formId}&recordId={recordId}&openApproval=true&stepIndex={stepIndex}
      // componentName: ApprovalListComponent、JSAIの場合はworkApplicationComponent
      // openApproval=true: 標記要打開簽核視窗而不是編輯視窗
      // stepIndex: 要顯示的簽核步驟索引（第一關為 stepIndex 參數值）
      if (recordId && assistantId) {
        let url = `${baseUrl}/assistant?id=${assistantId}`;

        // 加入 component 參數（動的に決定）
        url += `&component=${componentName}`;

        // 加入 formId（表單配置 ID）
        if (this.formid) {
          url += `&formId=${this.formid}`;
        }

        // 加入 recordId（表單記錄 ID）
        url += `&recordId=${recordId}`;

        // 加入 openApproval 標記（表示要打開簽核視窗）
        url += `&openApproval=true`;

        // 加入 stepIndex（簽核步驟索引）
        url += `&stepIndex=${stepIndex}`;

        console.log('✅ [第一關通知] 生成連結:', url, {
          recordId,
          openApproval: true,
          stepIndex
        });
        return url;
      } else if (assistantId) {
        // assistantId is define
        return `${baseUrl}/assistant?id=${assistantId}&component=${componentName}`;
      } else {
        // fallback
        return `${baseUrl}/assistant?component=${componentName}`;
      }
    } catch (error) {
      console.warn(this.i18nService.translate('dynamic_form.console.generate_task_link_failed') + ':', error);
      // fallback: group_id
      const groupId = environment.group_id || 'JSAI';
      const componentName = (groupId === 'IAD')
        ? 'FormApprovalListComponent'
        : 'workApplicationComponent';
      return `${window.location.origin}/assistant?component=${componentName}`;
    }
  }

  /**
   * 取得來源元件的值（用於條件顯示）
   */
  getSourceComponentValue(sourceComponentId: string): any {
    if (!sourceComponentId || !this.formGroup) {
      return null;
    }

    const control = this.formGroup.get(sourceComponentId);
    return control ? control.value : null;
  }

  /**
   * 取得 environment 中的 companyid（確保在模板渲染時可立即取得）
   */
  getEnvironmentCompanyId(): string {
    return environment.companyid;
  }

  /**
   * 更新表單資料緩存，供自動編號元件使用
   */
  private updateFormDataCache(): void {
    try {
      if (!this.formGroup) {
        this.formDataForAutoNumbering = {};
        return;
      }

      // 取得原始表單值
      const rawFormData = this.formGroup.value;

      // 計算當前資料的哈希值，用於檢查是否有變更
      const currentHash = JSON.stringify(rawFormData);

      // 如果資料沒有變更，直接返回緩存的資料
      if (currentHash === this._lastFormDataHash) {
        return;
      }


      // 處理表單資料，確保格式符合自動編號元件的需求
      const formData: { [key: string]: any } = {};

      Object.keys(rawFormData).forEach(key => {
        const value = rawFormData[key];
        const field = this.config.find(f => f.name === key);

        if (!field) {
          formData[key] = value;
          return;
        }

        // 根據不同元件類型處理資料格式
        switch (field.type) {
          case 'radiobutton':
            // 處理 radio button 資料
            if (value && typeof value === 'object') {
              formData[key] = {
                type: 'radiobutton',
                value: value.selectedValue || value.key || value,
                label: value.selectedLabel || value.value || this.getRadioOptionLabel(field, value.selectedValue || value.key || value)
              };
            } else if (typeof value === 'string') {
              formData[key] = {
                type: 'radiobutton',
                value: value,
                label: this.getRadioOptionLabel(field, value)
              };
            } else {
              formData[key] = {
                type: 'radiobutton',
                value: value,
                label: value
              };
            }
            break;

          case 'select':
            // 處理 select 資料
            if (value && typeof value === 'object') {
              formData[key] = {
                type: 'select',
                value: value.key || value.value || value,
                label: value.value || value.label || value
              };
            } else {
              formData[key] = {
                type: 'select',
                value: value,
                label: this.getSelectOptionLabel(field, value)
              };
            }
            break;

          case 'multi-checkbox':
            // 處理多選框資料
            if (Array.isArray(value)) {
              formData[key] = {
                type: 'multi-checkbox',
                value: value.map(v => v.key || v.value || v),
                selectedItems: value.map(v => v.value || v.label || v)
              };
            } else {
              formData[key] = {
                type: 'multi-checkbox',
                value: value,
                selectedItems: value
              };
            }
            break;

          default:
            // 其他類型直接使用原始值
            formData[key] = {
              type: field.type,
              value: value
            };
            break;
        }
      });

      // 更新緩存
      this.formDataForAutoNumbering = formData;
      this._lastFormDataHash = currentHash;

    } catch (error) {
      console.error(this.i18nService.translate('dynamic_form.console.update_form_data_cache_error') + ':', error);
      this.formDataForAutoNumbering = {};
    }
  }

  /**
   * 取得當前表單資料，供自動編號元件使用（已棄用，改用屬性綁定）
   * @deprecated 使用 formDataForAutoNumbering 屬性替代
   */
  getFormData(): { [key: string]: any } {
    this.updateFormDataCache();
    return this.formDataForAutoNumbering;
  }

  /**
   * 取得 radio button 選項的標籤
   */
  private getRadioOptionLabel(field: FormFieldConfig, value: string): string {
    if (!field.options || !value) return value || '';

    const option = field.options.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * 取得 select 選項的標籤
   */
  private getSelectOptionLabel(field: FormFieldConfig, value: string): string {
    if (!field.options || !value) return value || '';

    const option = field.options.find(opt => opt.value === value);
    return option ? option.label : value;
  }

}
